// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// Non-component exports extracted from OpenEstimate.tsx to allow Vite HMR
// fast refresh (which requires a module's default export to be the only React
// component, with no other non-component exports in that file).

// ── Type definitions shared with OpenEstimate.tsx ──
// Exported here so there's a single source of truth — duplicate interface
// declarations in two files were creating "two types with the same name"
// errors and silently allowing shape drift.
export interface BidScope {
  id:string; name:string;
  divisionIds:string[];
  /**
   * Subcategory ids this package covers. When set, these decide the budget
   * and divisionIds is only history.
   *
   * A package is let under a cost code, not a division: Div 06 is rough
   * carpentry AND millwork, Div 09 is four trades. Budgeting a framing bid
   * against the whole of Div 06 reported it 53% under on the sandbox project.
   * Optional so every scope that predates it keeps working.
   */
  subIds?:string[];
}
export interface BidPartner { id:string; name:string; trade:string; contact:string; phone?:string; email?:string; notes:string }
export interface BidItem { id:string; description:string; qty:number; unit:string; unitCost:number; notes:string; altType?:string }
export interface Bid { id:string; partnerId:string; scopeId:string; version:number; date:string; status:"Active"|"Superseded"|"Rejected"|"Pending"; items:BidItem[]; pdfName:string; pdfStoragePath?:string; pdfUrl?:string; notes:string }
export interface LevelingAdjustment {
  id: string;
  scopeId: string;
  description: string;
  partners: Record<string, { included: boolean; amount: number; }>
}

export const ALL_DIVISIONS = [
  {id:"div1", code:"Div 01",name:"General Requirements",          enabled:true},
  {id:"div2", code:"Div 02",name:"Existing Conditions",           enabled:true},
  {id:"div3", code:"Div 03",name:"Concrete",                      enabled:true},
  {id:"div4", code:"Div 04",name:"Masonry",                       enabled:true},
  {id:"div5", code:"Div 05",name:"Metals",                        enabled:true},
  {id:"div6", code:"Div 06",name:"Wood, Plastics & Composites",   enabled:true},
  {id:"div7", code:"Div 07",name:"Thermal & Moisture Protection", enabled:true},
  {id:"div8", code:"Div 08",name:"Openings",                      enabled:true},
  {id:"div9", code:"Div 09",name:"Finishes",                      enabled:true},
  {id:"div10",code:"Div 10",name:"Specialties",                   enabled:false},
  {id:"div11",code:"Div 11",name:"Equipment",                     enabled:false},
  {id:"div12",code:"Div 12",name:"Furnishings",                   enabled:false},
  {id:"div13",code:"Div 13",name:"Special Construction",          enabled:true},
  {id:"div14",code:"Div 14",name:"Conveying Equipment",           enabled:false},
  {id:"div21",code:"Div 21",name:"Fire Suppression",              enabled:false},
  {id:"div22",code:"Div 22",name:"Plumbing",                      enabled:true},
  {id:"div23",code:"Div 23",name:"HVAC",                          enabled:true},
  {id:"div25",code:"Div 25",name:"Integrated Automation",         enabled:false},
  {id:"div26",code:"Div 26",name:"Electrical",                    enabled:true},
  {id:"div27",code:"Div 27",name:"Communications",                enabled:false},
  {id:"div28",code:"Div 28",name:"Electronic Safety & Security",  enabled:false},
  {id:"div31",code:"Div 31",name:"Earthwork",                     enabled:false},
  {id:"div32",code:"Div 32",name:"Exterior Improvements",         enabled:true},
  {id:"div33",code:"Div 33",name:"Utilities",                     enabled:false},
];

export const DEFAULT_SUBS = {
  div1:[{id:"s_pf",name:"Permit Fees",costCode:"01-100"},{id:"s_gc",name:"General Conditions",costCode:"01-200"}],
  div2:[{id:"s_dem",name:"Demolition",costCode:"02-050"},{id:"s_sp",name:"Site Prep",costCode:"02-100"}],
  div3:[{id:"s_bc",name:"Building Concrete",costCode:"03-100"},{id:"s_sc",name:"Site Concrete",costCode:"03-200"}],
  div4:[{id:"s_ma",name:"Masonry",costCode:"04-100"}],
  div5:[{id:"s_ss",name:"Structural Steel",costCode:"05-100"},{id:"s_mm",name:"Misc. Metals",costCode:"05-500"}],
  div6:[{id:"s_rc",name:"Rough Carpentry",costCode:"06-100"},{id:"s_fc",name:"Finish Carpentry",costCode:"06-200"}],
  div7:[{id:"s_rf",name:"Roofing",costCode:"07-500"},{id:"s_cl",name:"Cladding & Siding",costCode:"07-460"},{id:"s_in",name:"Insulation",costCode:"07-200"},{id:"s_wp",name:"Waterproofing",costCode:"07-100"}],
  div8:[{id:"s_dw",name:"Doors & Windows",costCode:"08-100"}],
  div9:[{id:"s_dr",name:"Drywall",costCode:"09-250"},{id:"s_pt",name:"Painting",costCode:"09-900"},{id:"s_ti",name:"Tile",costCode:"09-300"},{id:"s_st",name:"Stone & Slabs",costCode:"09-350"},{id:"s_hw",name:"Hardwood Flooring",costCode:"09-640"},{id:"s_pla",name:"Plaster & Specialty Finishes",costCode:"09-230"}],
  div10:[],div11:[],div12:[],
  div13:[{id:"s_po",name:"Pool Construction",costCode:"13-150"},{id:"s_tc",name:"Tennis Court",costCode:"13-800"}],
  div14:[],div21:[],
  div22:[{id:"s_pl",name:"Plumbing",costCode:"22-000"}],
  div23:[{id:"s_hv",name:"HVAC",costCode:"23-000"}],
  div25:[],
  div26:[{id:"s_el",name:"Electrical",costCode:"26-000"}],
  div27:[],div28:[],div31:[],
  div32:[{id:"s_lh",name:"Landscaping & Hardscape",costCode:"32-900"}],
  div33:[],
};

export const DEFAULT_PRESETS = [
  {id:"preset_sfr",  name:"Single Family Residential",  overhead:10, profit:8,  contingency:5, divisions:["div1","div2","div3","div4","div5","div6","div7","div8","div9","div13","div22","div23","div26","div32"], builtIn:true},
  {id:"preset_mf",   name:"Multifamily",                overhead:12, profit:9,  contingency:5, divisions:["div1","div2","div3","div4","div5","div6","div7","div8","div9","div22","div23","div26","div32"], builtIn:true},
  {id:"preset_com",  name:"Commercial / Institutional", overhead:14, profit:10, contingency:7, divisions:["div1","div2","div3","div4","div5","div6","div7","div8","div9","div10","div11","div22","div23","div25","div26","div27","div28","div31","div32"], builtIn:true},
  {id:"preset_blank",name:"Blank",                      overhead:10, profit:8,  contingency:5, divisions:[], builtIn:true},
];

export function buildFreshProject(divisionSettings,subSettings){
  const order=ALL_DIVISIONS.map(d=>d.id);
  const enabledIds=divisionSettings.filter(x=>x.enabled).map(x=>x.id);
  const divisions=enabledIds.map(id=>{
    const meta=ALL_DIVISIONS.find(d=>d.id===id);
    const subs=(subSettings[id]||DEFAULT_SUBS[id]||[]).map(s=>({...s,rows:[]}));
    return {id,code:meta.code,name:meta.name,subcategories:subs};
  }).sort((a,b)=>order.indexOf(a.id)-order.indexOf(b.id));
  return {divisions,itemState:{}};
}

// ── Sandbox site plan SVG (generated — no real project imagery) ──
function buildSandboxSiteLogistics(){
  // A civil site plan on an ARCH D sheet, 36" × 24", drawn at 1" = 20'.
  //
  // Those three facts fix everything else: 1200 viewBox units across 36
  // inches is 33.333 units to the inch, so one foot of ground is 1.667 units
  // and the sheet covers 720' × 480' of it.
  //
  // It used to say 1" = 20' while being drawn at 4.8 units to the foot, which
  // on a sheet this size is 1" = 6.9'. Everything inherited the error: type
  // set for a 12" sheet, a title block ten inches wide, line weights at three
  // points, and a graphic scale bar that disagreed with its own drawing.
  // Nothing below is written in units — it is written in feet, or in points
  // for the things that belong to the paper rather than to the ground, and
  // converted once, here.
  const UPI=1200/36;              // viewBox units per paper inch
  const FT_PER_IN=20;             // the scale printed on the sheet
  const UPF=UPI/FT_PER_IN;        // units per foot of ground
  const n=(v)=>+v.toFixed(2);
  const IN=(i)=>n(i*UPI);         // paper inches → units
  const PT=(p)=>n(p*UPI/72);      // points → units: type sizes and line weights
  // The drawing window, with the ground's origin at its top-left corner.
  const WIN={x:IN(3.95),y:IN(0.55),w:IN(26.55),h:IN(22.9)};
  const X=(ft)=>n(WIN.x+ft*UPF);  // ground X → units
  const Y=(ft)=>n(WIN.y+ft*UPF);  // ground Y → units
  const L=(ft)=>n(ft*UPF);        // a ground length → units
  const GW=FT_PER_IN*26.55;       // 531' of ground across the window
  const GH=FT_PER_IN*22.9;        // 458' down it

  // ── The lot ──
  // A 380' × 330' corner parcel — 2.88 acres — with Evergreen Lane across
  // the north and Spruce Street down the west. Kept in one place because the
  // boundary, the setbacks, the dimension strings and every markup below are
  // all statements about the same four numbers.
  const LOT={w:72,e:492,n:84,s:414};
  const SETBACK={front:30,rear:20,side:15};

  const INK="#1a2840", PAVE="#e9e5db", PAVE_EDGE="#b9b2a2";
  const ROAD="#ddd9cf", GRASS="#ffffff", BLDG="#dfe4ef";

  const tree=(x,y,r,kind)=>kind==="ever"
    ?`<circle cx="${X(x)}" cy="${Y(y)}" r="${L(r)}" fill="none" stroke="#6f8a68" stroke-width="${PT(0.6)}" stroke-dasharray="${PT(4)},${PT(2.5)}"/>`
    :`<circle cx="${X(x)}" cy="${Y(y)}" r="${L(r)}" fill="none" stroke="#9aaa90" stroke-width="${PT(0.5)}" stroke-dasharray="${PT(3)},${PT(2)}"/>`;
  const TREES=[
    [95,100,14,"dec"],[130,96,11,"dec"],[168,102,13,"dec"],[205,97,10,"dec"],
    [258,101,12,"dec"],[300,98,10,"dec"],[384,101,13,"dec"],[462,97,11,"dec"],
    [96,150,12,"dec"],[92,196,13,"dec"],[97,246,11,"dec"],[94,296,12,"dec"],
    [476,150,11,"dec"],[480,330,13,"dec"],[470,392,12,"dec"],
    [140,352,18,"ever"],[210,366,16,"ever"],[278,358,17,"ever"],[352,384,15,"ever"],
  ].map(t=>tree(t[0],t[1],t[2],t[3])).join("");

  // The engineer's linetype key, down the left edge the way a civil sheet
  // carries one. The sample lines are drawn in the weights the drawing uses,
  // so the key is the drawing's own rather than a picture of one.
  const LT_ROWS=[
    ["PROPERTY LINE","#c84020",PT(1),`${PT(10)},${PT(4)}`],
    ["ADJOINER / RIGHT-OF-WAY","#9a9284",PT(0.8),""],
    ["BUILDING SETBACK","#6a8ab0",PT(0.7),`${PT(6)},${PT(3.5)}`],
    ["EXISTING CONTOUR","#b8a888",PT(0.6),`${PT(2.5)},${PT(2.5)}`],
    ["EDGE OF PAVEMENT",PAVE_EDGE,PT(0.8),""],
    ["CONCRETE WALK","#c0b8a8",PT(0.7),""],
    ["WATER MAIN","#3070c8",PT(0.8),`${PT(8)},${PT(3)}`],
    ["SANITARY SEWER","#806020",PT(0.8),`${PT(4)},${PT(3)}`],
    ["TREE TO REMAIN","#6f8a68",PT(0.6),`${PT(4)},${PT(2.5)}`],
    ["LIMIT OF DISTURBANCE","#c84020",PT(0.9),`${PT(5)},${PT(3)},${PT(1)},${PT(3)}`],
  ].map((r,i)=>{
    const y=IN(1.55+i*0.42);
    return `<line x1="${IN(0.75)}" y1="${y}" x2="${IN(1.85)}" y2="${y}" stroke="${r[1]}" stroke-width="${r[2]}" stroke-dasharray="${r[3]}"/>`
      +`<text x="${IN(2.0)}" y="${n(y+PT(2.2))}" font-family="Arial,sans-serif" font-size="${PT(5.5)}" fill="#444">${r[0]}</text>`;
  }).join("");

  const REVS=[["01","ISSUED FOR PERMIT","03-10-25"],["02","LOGISTICS UPDATE","04-15-25"]]
    .map((r,i)=>{
      const y=IN(8.1+i*0.34);
      return `<text x="${IN(31.0)}" y="${y}" font-family="Arial,sans-serif" font-size="${PT(5.5)}" fill="${INK}">${r[0]}</text>`
        +`<text x="${IN(31.5)}" y="${y}" font-family="Arial,sans-serif" font-size="${PT(5.5)}" fill="${INK}">${r[1]}</text>`
        +`<text x="${IN(35.2)}" y="${y}" text-anchor="end" font-family="Arial,sans-serif" font-size="${PT(5.5)}" fill="${INK}">${r[2]}</text>`;
    }).join("");

  // Short lines on purpose: the column they sit in is three inches wide, and
  // a note that runs past it would be printed over the drawing.
  const NOTES=[
    "1.  BOUNDARY AND TOPOGRAPHY FROM FIELD SURVEY BY WFB,",
    "     02-18-2025.  VERTICAL DATUM NAVD88.",
    "2.  CONTOUR INTERVAL 2'.  BENCHMARK: TOP NUT OF HYDRANT",
    "     AT EVERGREEN LANE, ELEV. 141.86'.",
    "3.  UNDERGROUND UTILITIES ARE FROM RECORD DRAWINGS AND",
    "     SURFACE EVIDENCE.  LOCATIONS ARE APPROXIMATE.",
    "4.  CONTRACTOR SHALL VERIFY ALL UTILITIES IN THE FIELD",
    "     PRIOR TO EXCAVATION.  CALL 811 BEFORE YOU DIG.",
    "5.  EROSION CONTROLS SHALL BE IN PLACE BEFORE ANY SITE",
    "     DISTURBANCE AND MAINTAINED UNTIL STABILIZATION.",
    "6.  TREES TO REMAIN SHALL BE FENCED AT THE DRIP LINE.",
  ].map((t,i)=>`<text x="${IN(0.75)}" y="${IN(6.75+i*0.26)}" font-family="Arial,sans-serif" font-size="${PT(4.8)}" fill="#444">${t}</text>`).join("");

  const svg=`<svg xmlns="http://www.w3.org/2000/svg" width="3456" height="2304" viewBox="0 0 1200 800">
  <defs>
    <pattern id="hatch" patternUnits="userSpaceOnUse" width="${IN(0.1)}" height="${IN(0.1)}" patternTransform="rotate(45)">
      <line x1="0" y1="0" x2="0" y2="${IN(0.1)}" stroke="#a8b8c8" stroke-width="${PT(0.4)}"/>
    </pattern>
  </defs>

  <rect width="1200" height="800" fill="#ffffff"/>
  <rect x="${IN(0.25)}" y="${IN(0.25)}" width="${IN(35.5)}" height="${IN(23.5)}" fill="none" stroke="${INK}" stroke-width="${PT(2.5)}"/>
  <rect x="${IN(0.4)}" y="${IN(0.4)}" width="${IN(35.2)}" height="${IN(23.2)}" fill="none" stroke="${INK}" stroke-width="${PT(0.6)}"/>

  <!-- ── LINETYPE LEGEND (left edge) ── -->
  <text x="${IN(0.75)}" y="${IN(1.1)}" font-family="Arial,sans-serif" font-size="${PT(7)}" font-weight="bold" fill="${INK}" letter-spacing="0.5">LINETYPE LEGEND</text>
  <line x1="${IN(0.75)}" y1="${IN(1.22)}" x2="${IN(3.6)}" y2="${IN(1.22)}" stroke="${INK}" stroke-width="${PT(0.7)}"/>
  ${LT_ROWS}

  <!-- ── GENERAL NOTES (left edge, under the key) ── -->
  <text x="${IN(0.75)}" y="${IN(6.3)}" font-family="Arial,sans-serif" font-size="${PT(7)}" font-weight="bold" fill="${INK}" letter-spacing="0.5">GENERAL NOTES</text>
  <line x1="${IN(0.75)}" y1="${IN(6.42)}" x2="${IN(3.6)}" y2="${IN(6.42)}" stroke="${INK}" stroke-width="${PT(0.7)}"/>
  ${NOTES}

  <!-- ── TITLE BLOCK (right edge, the way a D sheet carries one) ── -->
  <line x1="${IN(30.6)}" y1="${IN(0.4)}" x2="${IN(30.6)}" y2="${IN(23.6)}" stroke="${INK}" stroke-width="${PT(1)}"/>
  <circle cx="${IN(33.1)}" cy="${IN(1.45)}" r="${IN(0.5)}" fill="none" stroke="${INK}" stroke-width="${PT(1)}"/>
  <text x="${IN(33.1)}" y="${IN(1.62)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(13)}" font-weight="bold" fill="${INK}">W</text>
  <text x="${IN(33.1)}" y="${IN(2.4)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(10)}" font-weight="bold" fill="${INK}" letter-spacing="0.5">WFB CIVIL ENGINEERS</text>
  <text x="${IN(33.1)}" y="${IN(2.66)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5.5)}" fill="#666" letter-spacing="0.3">CIVIL ENGINEERING + LAND SURVEYING</text>
  <text x="${IN(33.1)}" y="${IN(2.88)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5.5)}" fill="#666">41 MASON STREET · GREENWICH, CT 06830</text>
  <line x1="${IN(31.0)}" y1="${IN(3.15)}" x2="${IN(35.2)}" y2="${IN(3.15)}" stroke="${INK}" stroke-width="${PT(0.6)}"/>

  <text x="${IN(33.1)}" y="${IN(3.8)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(6)}" fill="#777" letter-spacing="0.8">PROJECT</text>
  <text x="${IN(33.1)}" y="${IN(4.2)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(9)}" font-weight="bold" fill="${INK}">100 EVERGREEN LANE</text>
  <text x="${IN(33.1)}" y="${IN(4.5)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(7)}" fill="#555">GREENWICH, CONNECTICUT</text>
  <line x1="${IN(31.0)}" y1="${IN(4.8)}" x2="${IN(35.2)}" y2="${IN(4.8)}" stroke="${INK}" stroke-width="${PT(0.6)}"/>

  <text x="${IN(33.1)}" y="${IN(5.4)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(6)}" fill="#777" letter-spacing="0.8">SHEET TITLE</text>
  <text x="${IN(33.1)}" y="${IN(5.82)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(8.5)}" font-weight="bold" fill="${INK}">CONSTRUCTION</text>
  <text x="${IN(33.1)}" y="${IN(6.12)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(8.5)}" font-weight="bold" fill="${INK}">LOGISTICS PLAN</text>
  <text x="${IN(33.1)}" y="${IN(6.42)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5.5)}" fill="#8a3030">SCHEMATIC — NOT FOR CONSTRUCTION</text>
  <line x1="${IN(31.0)}" y1="${IN(6.7)}" x2="${IN(35.2)}" y2="${IN(6.7)}" stroke="${INK}" stroke-width="${PT(0.6)}"/>

  <text x="${IN(31.0)}" y="${IN(7.3)}" font-family="Arial,sans-serif" font-size="${PT(6)}" fill="#777" letter-spacing="0.8">REVISIONS</text>
  <line x1="${IN(31.0)}" y1="${IN(7.45)}" x2="${IN(35.2)}" y2="${IN(7.45)}" stroke="${INK}" stroke-width="${PT(0.4)}"/>
  <text x="${IN(31.0)}" y="${IN(7.78)}" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#999">NO.</text>
  <text x="${IN(31.5)}" y="${IN(7.78)}" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#999">DESCRIPTION</text>
  <text x="${IN(35.2)}" y="${IN(7.78)}" text-anchor="end" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#999">DATE</text>
  ${REVS}
  <line x1="${IN(31.0)}" y1="${IN(9.0)}" x2="${IN(35.2)}" y2="${IN(9.0)}" stroke="${INK}" stroke-width="${PT(0.6)}"/>

  <text x="${IN(31.0)}" y="${IN(9.6)}" font-family="Arial,sans-serif" font-size="${PT(4.8)}" fill="#888">THIS DRAWING IS AN INSTRUMENT OF SERVICE AND</text>
  <text x="${IN(31.0)}" y="${IN(9.85)}" font-family="Arial,sans-serif" font-size="${PT(4.8)}" fill="#888">REMAINS THE PROPERTY OF THE ENGINEER.  IT MAY</text>
  <text x="${IN(31.0)}" y="${IN(10.1)}" font-family="Arial,sans-serif" font-size="${PT(4.8)}" fill="#888">NOT BE REPRODUCED OR USED ON ANOTHER PROJECT</text>
  <text x="${IN(31.0)}" y="${IN(10.35)}" font-family="Arial,sans-serif" font-size="${PT(4.8)}" fill="#888">WITHOUT WRITTEN CONSENT.</text>

  <text x="${IN(31.8)}" y="${IN(14.2)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(9)}" font-weight="bold" fill="${INK}" letter-spacing="1.2" transform="rotate(-90,${IN(31.8)},${IN(14.2)})">100 EVERGREEN LANE · GREENWICH</text>
  <text x="${IN(32.3)}" y="${IN(14.2)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(6)}" fill="#777" letter-spacing="0.8" transform="rotate(-90,${IN(32.3)},${IN(14.2)})">EROSION AND SEDIMENT CONTROL · CONSTRUCTION LOGISTICS</text>

  <circle cx="${IN(33.9)}" cy="${IN(17.6)}" r="${IN(1.25)}" fill="none" stroke="#8a3030" stroke-width="${PT(0.8)}"/>
  <circle cx="${IN(33.9)}" cy="${IN(17.6)}" r="${IN(1.1)}" fill="none" stroke="#8a3030" stroke-width="${PT(0.5)}"/>
  <text x="${IN(33.9)}" y="${IN(17.05)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(4.8)}" fill="#8a3030" letter-spacing="0.4">PROFESSIONAL ENGINEER</text>
  <text x="${IN(33.9)}" y="${IN(17.65)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(7)}" font-weight="bold" fill="#8a3030">W. F. BRANNIGAN</text>
  <text x="${IN(33.9)}" y="${IN(17.95)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#8a3030">No. 24871 · CONNECTICUT</text>

  <line x1="${IN(31.0)}" y1="${IN(20.4)}" x2="${IN(35.2)}" y2="${IN(20.4)}" stroke="${INK}" stroke-width="${PT(0.6)}"/>
  <text x="${IN(31.0)}" y="${IN(20.78)}" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#999">DRAWN</text>
  <text x="${IN(32.4)}" y="${IN(20.78)}" font-family="Arial,sans-serif" font-size="${PT(6.5)}" fill="${INK}">WFB / AJK</text>
  <text x="${IN(33.7)}" y="${IN(20.78)}" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#999">CHECKED</text>
  <text x="${IN(35.2)}" y="${IN(20.78)}" text-anchor="end" font-family="Arial,sans-serif" font-size="${PT(6.5)}" fill="${INK}">RDS</text>
  <text x="${IN(31.0)}" y="${IN(21.22)}" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#999">DATE</text>
  <text x="${IN(32.4)}" y="${IN(21.22)}" font-family="Arial,sans-serif" font-size="${PT(6.5)}" fill="${INK}">04-15-2025</text>
  <text x="${IN(33.7)}" y="${IN(21.22)}" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#999">JOB NO.</text>
  <text x="${IN(35.2)}" y="${IN(21.22)}" text-anchor="end" font-family="Arial,sans-serif" font-size="${PT(6.5)}" fill="${INK}">2025-047</text>

  <text x="${IN(31.0)}" y="${IN(21.8)}" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#999">SCALE</text>
  <text x="${IN(32.4)}" y="${IN(21.8)}" font-family="Arial,sans-serif" font-size="${PT(8)}" font-weight="bold" fill="${INK}">1" = 20'</text>
  <g transform="translate(${IN(31.0)},${IN(22.4)})">
    <rect x="0" y="0" width="${L(20)}" height="${PT(5)}" fill="${INK}"/>
    <rect x="${L(20)}" y="0" width="${L(20)}" height="${PT(5)}" fill="#ffffff" stroke="${INK}" stroke-width="${PT(0.5)}"/>
    <rect x="${L(40)}" y="0" width="${L(20)}" height="${PT(5)}" fill="${INK}"/>
    <rect x="${L(60)}" y="0" width="${L(20)}" height="${PT(5)}" fill="#ffffff" stroke="${INK}" stroke-width="${PT(0.5)}"/>
    <text x="0" y="${PT(11)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="${INK}">0</text>
    <text x="${L(40)}" y="${PT(11)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="${INK}">40'</text>
    <text x="${L(80)}" y="${PT(11)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="${INK}">80'</text>
  </g>
  <line x1="${IN(31.0)}" y1="${IN(22.9)}" x2="${IN(35.2)}" y2="${IN(22.9)}" stroke="${INK}" stroke-width="${PT(0.6)}"/>
  <text x="${IN(31.0)}" y="${IN(23.35)}" font-family="Arial,sans-serif" font-size="${PT(6)}" fill="#999" letter-spacing="0.8">SHEET</text>
  <text x="${IN(35.2)}" y="${IN(23.4)}" text-anchor="end" font-family="Arial,sans-serif" font-size="${PT(16)}" font-weight="bold" fill="${INK}">C-3.1</text>

  <!-- ── THE DRAWING ──
       A corner lot: Evergreen Lane across the north, Spruce Street down the
       west side. Two streets is what orients a reader instantly — which way
       the house faces, where the trucks come in, and which side the back
       yard is on. Everything here is placed in feet of ground. -->
  <rect x="${WIN.x}" y="${WIN.y}" width="${WIN.w}" height="${WIN.h}" fill="${GRASS}"/>

  <!-- Evergreen Lane, 50' right-of-way across the frontage -->
  <rect x="${X(0)}" y="${Y(0)}" width="${L(GW)}" height="${L(50)}" fill="${ROAD}"/>
  <line x1="${X(0)}" y1="${Y(25)}" x2="${X(GW)}" y2="${Y(25)}" stroke="#e0c030" stroke-width="${PT(1)}" stroke-dasharray="${PT(9)},${PT(5)}"/>
  <line x1="${X(0)}" y1="${Y(50)}" x2="${X(GW)}" y2="${Y(50)}" stroke="#9a9284" stroke-width="${PT(1)}"/>
  <rect x="${X(0)}" y="${Y(50)}" width="${L(GW)}" height="${L(6)}" fill="#f2f0e8"/>
  <rect x="${X(0)}" y="${Y(56)}" width="${L(GW)}" height="${L(6)}" fill="#e8e4d8"/>
  <line x1="${X(0)}" y1="${Y(62)}" x2="${X(GW)}" y2="${Y(62)}" stroke="#b0a898" stroke-width="${PT(0.5)}"/>
  <text x="${X(250)}" y="${Y(20)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(9)}" fill="#8c8578" letter-spacing="1.2" font-weight="bold">EVERGREEN LANE</text>
  <text x="${X(250)}" y="${Y(31)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5.5)}" fill="#a39a88" letter-spacing="0.5">50' R.O.W. — PUBLIC</text>

  <!-- Spruce Street, down the west side -->
  <rect x="${X(0)}" y="${Y(0)}" width="${L(50)}" height="${L(GH)}" fill="${ROAD}"/>
  <line x1="${X(25)}" y1="${Y(0)}" x2="${X(25)}" y2="${Y(GH)}" stroke="#e0c030" stroke-width="${PT(1)}" stroke-dasharray="${PT(9)},${PT(5)}"/>
  <line x1="${X(50)}" y1="${Y(50)}" x2="${X(50)}" y2="${Y(GH)}" stroke="#9a9284" stroke-width="${PT(1)}"/>
  <rect x="${X(50)}" y="${Y(56)}" width="${L(6)}" height="${L(GH-56)}" fill="#f2f0e8"/>
  <rect x="${X(56)}" y="${Y(62)}" width="${L(6)}" height="${L(GH-62)}" fill="#e8e4d8"/>
  <line x1="${X(62)}" y1="${Y(62)}" x2="${X(62)}" y2="${Y(GH)}" stroke="#b0a898" stroke-width="${PT(0.5)}"/>
  <text x="${X(20)}" y="${Y(250)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(8.5)}" fill="#8c8578" letter-spacing="1" font-weight="bold" transform="rotate(-90,${X(20)},${Y(250)})">SPRUCE STREET</text>
  <path d="M ${X(62)} ${Y(62)} L ${X(56)} ${Y(62)} L ${X(56)} ${Y(56)} L ${X(50)} ${Y(56)} L ${X(50)} ${Y(50)} L ${X(0)} ${Y(50)}" fill="none" stroke="#b0a898" stroke-width="${PT(0.5)}"/>

  <!-- Utilities in the lane -->
  <line x1="${X(0)}" y1="${Y(34)}" x2="${X(GW)}" y2="${Y(34)}" stroke="#3070c8" stroke-width="${PT(0.8)}" stroke-dasharray="${PT(8)},${PT(3)}"/>
  <text x="${X(96)}" y="${Y(32)}" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#3070c8">W — 8" WATER MAIN</text>
  <line x1="${X(0)}" y1="${Y(42)}" x2="${X(GW)}" y2="${Y(42)}" stroke="#806020" stroke-width="${PT(0.8)}" stroke-dasharray="${PT(4)},${PT(3)}"/>
  <text x="${X(250)}" y="${Y(40)}" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#806020">S — 10" SANITARY SEWER</text>
  <circle cx="${X(190)}" cy="${Y(42)}" r="${L(2.5)}" fill="none" stroke="#806020" stroke-width="${PT(0.8)}"/>
  <text x="${X(196)}" y="${Y(48)}" font-family="Arial,sans-serif" font-size="${PT(4.5)}" fill="#806020">MH RIM 143.20</text>
  <circle cx="${X(70)}" cy="${Y(46)}" r="${L(2)}" fill="#c84020"/>
  <text x="${X(75)}" y="${Y(50)}" font-family="Arial,sans-serif" font-size="${PT(4.5)}" fill="#c84020">FH</text>

  <!-- Property boundary -->
  <path d="M ${X(LOT.w)} ${Y(LOT.n)} L ${X(LOT.e)} ${Y(LOT.n)} L ${X(LOT.e)} ${Y(LOT.s)} L ${X(LOT.w)} ${Y(LOT.s)} Z"
        fill="none" stroke="#c84020" stroke-width="${PT(1.2)}" stroke-dasharray="${PT(10)},${PT(4)}"/>
  <g stroke="#c84020" stroke-width="${PT(0.8)}" fill="none">
    <rect x="${n(X(LOT.w)-PT(2))}" y="${n(Y(LOT.n)-PT(2))}" width="${PT(4)}" height="${PT(4)}"/>
    <rect x="${n(X(LOT.e)-PT(2))}" y="${n(Y(LOT.n)-PT(2))}" width="${PT(4)}" height="${PT(4)}"/>
    <rect x="${n(X(LOT.e)-PT(2))}" y="${n(Y(LOT.s)-PT(2))}" width="${PT(4)}" height="${PT(4)}"/>
    <rect x="${n(X(LOT.w)-PT(2))}" y="${n(Y(LOT.s)-PT(2))}" width="${PT(4)}" height="${PT(4)}"/>
  </g>
  <text x="${n(X(LOT.w)-PT(7))}" y="${n(Y(LOT.n)-PT(3))}" font-family="Arial,sans-serif" font-size="${PT(4.5)}" fill="#c84020">IP</text>
  <text x="${n(X(LOT.e)+PT(2))}" y="${n(Y(LOT.n)-PT(3))}" font-family="Arial,sans-serif" font-size="${PT(4.5)}" fill="#c84020">IP</text>

  <!-- Lot dimensions -->
  <line x1="${X(LOT.w)}" y1="${Y(428)}" x2="${X(LOT.e)}" y2="${Y(428)}" stroke="${INK}" stroke-width="${PT(0.5)}"/>
  <line x1="${X(LOT.w)}" y1="${Y(425)}" x2="${X(LOT.w)}" y2="${Y(431)}" stroke="${INK}" stroke-width="${PT(0.5)}"/>
  <line x1="${X(LOT.e)}" y1="${Y(425)}" x2="${X(LOT.e)}" y2="${Y(431)}" stroke="${INK}" stroke-width="${PT(0.5)}"/>
  <text x="${X(282)}" y="${Y(426)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(6)}" fill="${INK}" font-weight="bold">420.00'</text>
  <line x1="${X(506)}" y1="${Y(LOT.n)}" x2="${X(506)}" y2="${Y(LOT.s)}" stroke="${INK}" stroke-width="${PT(0.5)}"/>
  <line x1="${X(503)}" y1="${Y(LOT.n)}" x2="${X(509)}" y2="${Y(LOT.n)}" stroke="${INK}" stroke-width="${PT(0.5)}"/>
  <line x1="${X(503)}" y1="${Y(LOT.s)}" x2="${X(509)}" y2="${Y(LOT.s)}" stroke="${INK}" stroke-width="${PT(0.5)}"/>
  <text x="${X(504)}" y="${Y(249)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(6)}" fill="${INK}" font-weight="bold" transform="rotate(-90,${X(504)},${Y(249)})">330.00'</text>

  <!-- Setbacks -->
  <g stroke="#6a8ab0" stroke-width="${PT(0.7)}" stroke-dasharray="${PT(6)},${PT(3.5)}" fill="none">
    <line x1="${X(LOT.w)}" y1="${Y(LOT.n+SETBACK.front)}" x2="${X(LOT.e)}" y2="${Y(LOT.n+SETBACK.front)}"/>
    <line x1="${X(LOT.w)}" y1="${Y(LOT.s-SETBACK.rear)}" x2="${X(LOT.e)}" y2="${Y(LOT.s-SETBACK.rear)}"/>
    <line x1="${X(LOT.w+SETBACK.side)}" y1="${Y(LOT.n)}" x2="${X(LOT.w+SETBACK.side)}" y2="${Y(LOT.s)}"/>
    <line x1="${X(LOT.e-SETBACK.side)}" y1="${Y(LOT.n)}" x2="${X(LOT.e-SETBACK.side)}" y2="${Y(LOT.s)}"/>
  </g>
  <text x="${X(92)}" y="${Y(111)}" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#6a8ab0">30' FRONT SETBACK</text>
  <text x="${X(92)}" y="${Y(401)}" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#6a8ab0">20' REAR SETBACK</text>

  <!-- Contours -->
  <g stroke="#b8a888" stroke-width="${PT(0.6)}" fill="none" stroke-dasharray="${PT(2.5)},${PT(2.5)}">
    <path d="M ${X(72)} ${Y(132)} C ${X(170)} ${Y(126)}, ${X(330)} ${Y(140)}, ${X(LOT.e)} ${Y(130)}"/>
    <path d="M ${X(72)} ${Y(300)} C ${X(180)} ${Y(292)}, ${X(340)} ${Y(312)}, ${X(LOT.e)} ${Y(300)}"/>
    <path d="M ${X(72)} ${Y(388)} C ${X(180)} ${Y(380)}, ${X(340)} ${Y(398)}, ${X(LOT.e)} ${Y(386)}"/>
  </g>
  <text x="${X(480)}" y="${Y(128)}" font-family="Arial,sans-serif" font-size="${PT(4.5)}" fill="#a89878">144</text>
  <text x="${X(480)}" y="${Y(298)}" font-family="Arial,sans-serif" font-size="${PT(4.5)}" fill="#a89878">142</text>
  <text x="${X(480)}" y="${Y(384)}" font-family="Arial,sans-serif" font-size="${PT(4.5)}" fill="#a89878">140</text>

  ${TREES}
  <text x="${X(140)}" y="${Y(353)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(4.5)}" fill="#6f8a68">30" OAK</text>
  <text x="${X(278)}" y="${Y(359)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(4.5)}" fill="#6f8a68">26" MAPLE</text>

  <!-- Driveway, motor court and apron -->
  <path d="M ${X(300)} ${Y(50)} L ${X(336)} ${Y(50)} L ${X(330)} ${Y(62)} L ${X(306)} ${Y(62)} Z" fill="#ddd8cc" stroke="${PAVE_EDGE}" stroke-width="${PT(0.7)}"/>
  <path d="M ${X(306)} ${Y(62)} L ${X(330)} ${Y(62)} L ${X(330)} ${Y(168)} L ${X(364)} ${Y(168)} L ${X(364)} ${Y(222)} L ${X(292)} ${Y(222)} L ${X(292)} ${Y(168)} L ${X(306)} ${Y(168)} Z"
        fill="${PAVE}" stroke="${PAVE_EDGE}" stroke-width="${PT(0.8)}"/>
  <text x="${X(328)}" y="${Y(214)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5.5)}" fill="#9a9080" letter-spacing="0.5">MOTOR COURT</text>

  <!-- Front walk -->
  <rect x="${X(198)}" y="${Y(120)}" width="${L(8)}" height="${L(50)}" fill="#ece8de" stroke="#c0b8a8" stroke-width="${PT(0.6)}"/>

  <!-- Main residence -->
  <rect x="${X(150)}" y="${Y(170)}" width="${L(108)}" height="${L(44)}" fill="${BLDG}" stroke="${INK}" stroke-width="${PT(1.6)}"/>
  <rect x="${X(150)}" y="${Y(170)}" width="${L(108)}" height="${L(44)}" fill="url(#hatch)" opacity="0.5"/>
  <line x1="${X(150)}" y1="${Y(192)}" x2="${X(258)}" y2="${Y(192)}" stroke="#8090a8" stroke-width="${PT(0.4)}" stroke-dasharray="${PT(5)},${PT(4)}"/>
  <line x1="${X(204)}" y1="${Y(170)}" x2="${X(204)}" y2="${Y(214)}" stroke="#8090a8" stroke-width="${PT(0.4)}" stroke-dasharray="${PT(5)},${PT(4)}"/>
  <text x="${X(204)}" y="${Y(188)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(8)}" font-weight="bold" fill="${INK}" letter-spacing="0.4">MAIN RESIDENCE</text>
  <text x="${X(204)}" y="${Y(196)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5.5)}" fill="#4a5870">± 10,000 SF · 2 STORIES + BSMT.</text>
  <text x="${X(204)}" y="${Y(203)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#6a7890">F.F.E. = 142.50'</text>
  <rect x="${X(196)}" y="${Y(167)}" width="${L(16)}" height="${L(3)}" fill="#cfd6e6" stroke="${INK}" stroke-width="${PT(0.8)}"/>

  <!-- Garage -->
  <rect x="${X(258)}" y="${Y(176)}" width="${L(34)}" height="${L(36)}" fill="${BLDG}" stroke="${INK}" stroke-width="${PT(1.3)}"/>
  <g stroke="#8090a8" stroke-width="${PT(0.7)}">
    <line x1="${X(263)}" y1="${Y(176)}" x2="${X(263)}" y2="${Y(180)}"/>
    <line x1="${X(275)}" y1="${Y(176)}" x2="${X(275)}" y2="${Y(180)}"/>
    <line x1="${X(287)}" y1="${Y(176)}" x2="${X(287)}" y2="${Y(180)}"/>
  </g>
  <text x="${X(275)}" y="${Y(196)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(6.5)}" font-weight="bold" fill="${INK}">GAR.</text>
  <text x="${X(275)}" y="${Y(203)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#6a7890">3-CAR</text>

  <!-- Rear terrace and the walk to the pool -->
  <rect x="${X(162)}" y="${Y(214)}" width="${L(90)}" height="${L(32)}" fill="#e6e1d6" stroke="${PAVE_EDGE}" stroke-width="${PT(0.8)}"/>
  <text x="${X(207)}" y="${Y(232)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(6)}" fill="#9a9080" letter-spacing="0.5">REAR TERRACE</text>
  <rect x="${X(252)}" y="${Y(252)}" width="${L(78)}" height="${L(6)}" fill="#ece8de" stroke="#c0b8a8" stroke-width="${PT(0.6)}"/>

  <!-- Pool house, pool terrace and pool -->
  <rect x="${X(334)}" y="${Y(252)}" width="${L(40)}" height="${L(30)}" fill="${BLDG}" stroke="${INK}" stroke-width="${PT(1.3)}"/>
  <text x="${X(354)}" y="${Y(266)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(6)}" font-weight="bold" fill="${INK}">POOL HSE.</text>
  <text x="${X(354)}" y="${Y(273)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5)}" fill="#6a7890">1,200 SF</text>
  <rect x="${X(326)}" y="${Y(290)}" width="${L(60)}" height="${L(70)}" fill="#e6e1d6" stroke="${PAVE_EDGE}" stroke-width="${PT(0.8)}"/>
  <rect x="${X(346)}" y="${Y(302)}" width="${L(20)}" height="${L(45)}" rx="${L(1.5)}" fill="#cfe4e4" stroke="#6a9aa8" stroke-width="${PT(1)}" stroke-dasharray="${PT(5)},${PT(3)}"/>
  <text x="${X(356)}" y="${Y(322)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5)}" font-weight="bold" fill="#4a7a88">POOL</text>
  <text x="${X(356)}" y="${Y(329)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(4.5)}" fill="#6a9aa8">FUTURE</text>
  <text x="${X(356)}" y="${Y(356)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(4.5)}" fill="#a89e8c">BLUESTONE TERRACE</text>

  <!-- The back yard is left open on purpose: it is where the logistics plan
       does its work, and labelling it would only get in the way. -->
  <text x="${X(265)}" y="${Y(318)}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(5.5)}" fill="#b8b0a4" letter-spacing="1">REAR YARD — LAWN</text>

  <!-- North arrow -->
  <g transform="translate(${IN(5.1)},${IN(1.75)})">
    <circle r="${IN(0.42)}" fill="#ffffff" fill-opacity="0.9" stroke="${INK}" stroke-width="${PT(0.8)}"/>
    <path d="M 0 ${n(-IN(0.3))} L ${IN(0.09)} ${IN(0.11)} L 0 ${IN(0.04)} L ${n(-IN(0.09))} ${IN(0.11)} Z" fill="${INK}"/>
    <text x="0" y="${n(-IN(0.47))}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${PT(6.5)}" font-weight="bold" fill="${INK}">N</text>
  </g>
</svg>`;

  // UTF-8 data URL — handles any Unicode cleanly
  const dataUrl="data:image/svg+xml;charset=utf-8,"+encodeURIComponent(svg);

  // ── The markups ──
  // Placed in feet of ground, on the same lot the drawing is of, and turned
  // into the percentages a plan stores. Written this way because "the trailer
  // sits fifty feet off the west line" is something somebody can check
  // against the drawing; "x: 21.0" is not.
  const pctX=(ft)=>+((X(ft)/1200)*100).toFixed(2);
  const pctY=(ft)=>+((Y(ft)/800)*100).toFixed(2);
  const at=(x,y)=>({x:pctX(x),y:pctY(y)});

  const icon=(id,type,x,y,label)=>({id,type,...at(x,y),label});
  const icons=[
    // Trailer and welfare in the front-west yard, off the drive and clear of
    // the house, which is where a superintendent actually parks one.
    icon("lg_trl1","field-trailer",   122, 128, "PM Field Trailer"),
    icon("lg_fa1", "first-aid",       158, 124, "First-Aid Station"),
    // Far enough apart that their two names do not print over each other.
    icon("lg_pp1", "porta-potty",     144, 150, "Porta-Potty"),
    icon("lg_pp2", "porta-potty",     168, 150, "Porta-Potty"),
    // Generator on the west margin, clear of the drive.
    icon("lg_gen1","generator",       100, 196, "400A Temp Generator"),
    // Dumpster and washout off the motor court, reachable from the lane
    // without a truck crossing the rear yard.
    icon("lg_dmp1","dumpster",        388, 184, "30 CY Dumpster"),
    icon("lg_cw1", "concrete-washout",402, 214, "Concrete Washout"),
  ];

  const line=(id,lineType,pts,label,strokeColor?,strokeWidth?,strokeDash?)=>
    ({id,lineType,points:pts.map(p=>at(p[0],p[1])),label,
      ...(strokeColor?{strokeColor}:{}),...(strokeWidth?{strokeWidth}:{}),...(strokeDash?{strokeDash}:{})});
  const lines=[
    // Limit of disturbance — just inside the property boundary.
    line("lg_ln_lod","lod",
      [[78,90],[486,90],[486,408],[78,408],[78,90]],
      "Limit of Disturbance"),
    // Temp security fence inside it, with the gate left open at the drive.
    line("lg_ln_fence","temp-fence",
      [[300,96],[86,96],[86,400],[478,400],[478,96],[340,96]],
      "Temp Security Fence"),
    // Haul route in off Evergreen Lane, down the drive to the motor court.
    line("lg_ln_haul","haul-route",
      [[318,54],[318,166],[330,192],[352,200]],
      "Primary Haul Route"),
    // Silt fence along the downhill boundary, which is the rear.
    line("lg_ln_ec","erosion-control",
      [[74,412],[490,412]],
      "Silt Fence / Erosion Control"),
    // Temp water off the main in the lane, down the west side to the house.
    line("lg_ln_water","utility-water",
      [[112,34],[112,180],[150,180]],
      "Temp Water Line"),
  ];

  const area=(id,areaType,x1,y1,x2,y2,label)=>
    ({id,areaType,points:[at(x1,y1),at(x1,y2),at(x2,y2),at(x2,y1)],label});
  const areas=[
    // Sized from the ground they cover rather than from the label: 70' × 30'
    // of laydown is 2,100 sf, and 80' × 40' of parking is eight cars and the
    // aisle they need.
    area("lg_ar_lay","laydown-area", 150,300, 220,330, "Material Laydown Zone"),
    area("lg_ar_pkg","parking-lot",  358,112, 438,152, "Contractor Parking — 8 spaces"),
  ];

  return {
    imageDataUrl:dataUrl,
    icons,
    lines,
    areas,
    circles:[],
    notes:"Phase 1 — Site Logistics Plan (Schematic)\n\nDrawn at 1\" = 20'. Every item is at its real footprint — the field trailer is 10' × 40', the roll-off 8' × 22' — so the plan answers whether things fit rather than just where they go.\n\nMaterial laydown (rear yard, 70' × 30', ~2,100 SF) is reserved for structural steel, masonry, windows and roofing. Sequence deliveries so nothing stacks.\n\nContractor parking (80' × 40', ~3,200 SF) is eight spaces and the aisle they need. Gate at Evergreen Lane is the single controlled entry.\n\nTrailer to be removed and lawn restored prior to Certificate of Occupancy. Porta-potties serviced weekly minimum.\n\nSilt fence and erosion control inspected after every measurable rain event. Concrete washout pumped at 75% capacity.\n\nThis plan is schematic and subject to revision as construction progresses.",
    // The sheet is 36" across and the drawing is at 1" = 20', so one percent
    // of its width is 7.2' of ground. Named rather than calibrated, so the
    // app can say which scale it is and not merely that there is one.
    scale:{preset:"i20",feetPerPct:7.2},
  };
}

export function buildSandboxProject(){
  const is={};
  // si: sandbox item helper — altType: "" (base), "Alt" (alternate; positive=add, negative=deduct)
  const si=(id,desc,qty,unit,unitCost,prop="p1",status="Hard Bid",altType="")=>{
    const item={type:"item",id,description:desc,qty,unit,unitCost,proposal:prop,notes:""};
    is[id]={description:desc,qty,unit,unitCost,included:true,costStatus:status,altType,proposal:prop,notes:""};
    return item;
  };
  const sub=(id,name,costCode,...items)=>({id,name,costCode,rows:items});
  const div=(divId,...subs)=>{const m=ALL_DIVISIONS.find(d=>d.id===divId);return {id:divId,code:m.code,name:m.name,subcategories:subs};};

  const divisions=[
    div("div1",
      sub("s_pf","Permit Fees","01-100",
        si("sb_pf1","Building Permit – Main Residence",1,"LS",85000,"p1","By Others"),
        si("sb_pf2","Soils Report & Special Inspections",1,"LS",18500),
      ),
      sub("s_gc","General Conditions","01-200",
        si("sb_gc1","Project Superintendent (18 months)",18,"MO",14500),
        si("sb_gc2","Temporary Facilities & Services",1,"LS",45000),
        si("sb_gc3","Builder's Risk & Liability Insurance",1,"LS",38000),
        si("sb_gc4","Site Safety & Security",18,"MO",1800),
      ),
    ),
    div("div2",
      sub("s_dem","Demolition","02-050",
        si("sb_dem1","Demolish Existing Residence",1,"LS",42000),
        si("sb_dem2","Hazmat Survey & Abatement",1,"LS",12500),
      ),
      sub("s_sp","Site Prep","02-100",
        si("sb_sp1","Site Grading & Earthwork",1,"LS",68000),
        si("sb_sp2","Erosion Control & SWPPP",1,"LS",8500),
        si("sb_sp3","Temporary Utilities & Access Road",1,"LS",14000),
      ),
    ),
    div("div3",
      sub("s_bc","Building Concrete","03-100",
        si("sb_bc1","Foundations & Footings – Main House",10000,"SF",22),
        si("sb_bc2","Foundations – Pool House",1200,"SF",24),
        si("sb_bc3","Basement Slab on Grade",2500,"SF",12),
        si("sb_bc4","ICF Basement Walls",1,"LS",95000),
      ),
      sub("s_sc","Site Concrete","03-200",
        si("sb_sc1","Driveway – Exposed Aggregate",3200,"SF",18,"p2"),
        si("sb_sc2","Entry Walks, Steps & Landing",1800,"SF",16,"p2"),
      ),
    ),
    div("div4",
      sub("s_ma","Masonry","04-100",
        si("sb_ma1","Stone Veneer – Exterior Façade",2800,"SF",42),
        si("sb_ma2","Fireplace Surrounds – Main House",2,"EA",18500),
        si("sb_ma3","Fireplace – Pool House",1,"EA",14500),
        si("sb_ma4","Exterior Stone Steps & Retaining Walls",1,"LS",28000,"p2","Needs Review"),
      ),
    ),
    div("div5",
      sub("s_ss","Structural Steel","05-100",
        si("sb_ss1","Steel Beams & Columns",1,"LS",185000),
        si("sb_ss2","Floating Steel Staircase – Main",1,"EA",42000),
      ),
      sub("s_mm","Misc. Metals","05-500",
        si("sb_mm1","Glass Guardrails & Balustrades",240,"LF",185),
        si("sb_mm2","Ornamental Iron Entry Gate",1,"EA",22000,"p2"),
      ),
    ),
    div("div6",
      sub("s_rc","Rough Carpentry","06-100",
        si("sb_rc1","Framing – Main House",10000,"SF",28),
        si("sb_rc2","Framing – Pool House",1200,"SF",30),
        si("sb_rc3","Roof Framing, Sheathing & Blocking",1,"LS",95000),
      ),
      sub("s_fc","Finish Carpentry","06-200",
        si("sb_fc1","Custom Millwork – Throughout",1,"LS",285000,"p1","Allowance"),
        si("sb_fc2","Kitchen Cabinetry & Built-ins",1,"LS",145000,"p1","Allowance"),
        si("sb_fc3","Pool House Cabinetry & Trim",1,"LS",38000),
        si("sb_fc4","Wood Paneling – Library & Study",1,"LS",42000,"p1","Needs Review"),
        // Add Alternates — scope enhancements
        si("sb_alt1","ADD: Home Theater & Media Room",1,"LS",85000,"p1","Hard Bid","Alt"),
        si("sb_alt2","ADD: Wine Cellar – Climate Controlled",1,"LS",52000,"p1","Hard Bid","Alt"),
      ),
    ),
    div("div7",
      sub("s_rf","Roofing","07-500",
        si("sb_rf1","Natural Slate Roof – Main House",8500,"SF",28),
        si("sb_rf2","Standing Seam Metal – Pool House",1400,"SF",24),
        // Deduct Alternate — substitute metal for slate
        si("sb_ded1","DEDUCT: Standing Seam Metal Roof vs. Slate",1,"LS",-119000,"p1","Hard Bid","Alt"),
      ),
      sub("s_cl","Cladding & Siding","07-460",
        si("sb_cl1","Stone & Stucco Exterior System",6500,"SF",32),
        si("sb_cl2","Clear Cedar Siding – Accent Areas",800,"SF",28),
      ),
      sub("s_in","Insulation","07-200",
        si("sb_in1","Spray Foam – Walls & Roof Assembly",11200,"SF",6.5),
        si("sb_in2","Blown-In Attic Insulation",3200,"SF",3.8),
      ),
      sub("s_wp","Waterproofing","07-100",
        si("sb_wp1","Foundation Waterproofing",1,"LS",48000),
        si("sb_wp2","Terrace & Deck Waterproofing Membrane",2200,"SF",22),
      ),
    ),
    div("div8",
      sub("s_dw","Doors & Windows","08-100",
        si("sb_dw1","Windows – Marvin Ultimate Series",85,"EA",3800),
        si("sb_dw2","Exterior Entry Doors – Mahogany Custom",8,"EA",6500),
        si("sb_dw3","Interior Doors – Solid Core 8'",42,"EA",1800),
        si("sb_dw4","Sliding Glass Wall Systems – Living",6,"EA",8500),
        si("sb_dw5","Pool House Windows & Doors",1,"LS",28000),
        // Deduct Alternate — standard windows vs. premium
        si("sb_ded2","DEDUCT: Standard Clad Windows vs. Marvin Ultimate",1,"LS",-85000,"p1","Hard Bid","Alt"),
      ),
    ),
    div("div9",
      sub("s_dr","Drywall","09-250",
        si("sb_dr1","Drywall & Metal Framing – Full Project",28000,"SF",5.5),
      ),
      sub("s_pt","Painting","09-900",
        si("sb_pt1","Interior Paint – Premium Finish",28000,"SF",5.8),
        si("sb_pt2","Exterior Paint, Stain & Sealer",8000,"SF",7.2),
      ),
      sub("s_ti","Tile","09-300",
        si("sb_ti1","Master Bath – Book-Matched Marble",680,"SF",48),
        si("sb_ti2","Full Baths 2 & 3 – Large Format Porcelain",520,"SF",28),
        si("sb_ti3","Full Baths 4 & 5 – Porcelain",480,"SF",26),
        si("sb_ti4","Pool House Bath",280,"SF",32),
        si("sb_ti5","Kitchen Backsplash – Calacatta Marble",180,"SF",52),
      ),
      sub("s_st","Stone & Slabs","09-350",
        si("sb_st1","Kitchen Countertops – White Quartzite",120,"LF",285,"p1","Allowance"),
        si("sb_st2","Island & Prep Counter",1,"LS",28500,"p1","Allowance"),
        si("sb_st3","Master Bath Countertops",1,"LS",18500,"p1","Allowance"),
        si("sb_st4","Stone Flooring – Entry, Foyer & Gallery",3200,"SF",38),
      ),
      sub("s_hw","Hardwood Flooring","09-640",
        si("sb_hw1","White Oak Hardwood – Living Areas",5800,"SF",24),
        si("sb_hw2","Pool House Hardwood Flooring",800,"SF",22),
      ),
      sub("s_pla","Plaster & Specialty Finishes","09-230",
        si("sb_pla1","Ornamental Plaster – Great Room & Entry",1,"LS",38000,"p1","Excluded"),
      ),
    ),
    div("div13",
      sub("s_po","Pool Construction","13-150",
        si("sb_po1","Swimming Pool – Gunite (650 SF)",1,"EA",185000,"p2"),
        si("sb_po2","Attached Spa & Water Features",1,"EA",42000,"p2"),
        si("sb_po3","Pool Equipment, Mechanical & Heating",1,"LS",28000,"p2"),
        // Add Alternate — pool enclosure
        si("sb_alt3","ADD: Motorized Pool Enclosure System",1,"LS",65000,"p2","Needs Review","Void"),
      ),
    ),
    div("div22",
      sub("s_pl","Plumbing","22-000",
        si("sb_pl1","Rough Plumbing – Main House",10000,"SF",22),
        si("sb_pl2","Rough Plumbing – Pool House",1200,"SF",18),
        si("sb_pl3","Plumbing Fixtures – Premium Allowance",1,"LS",85000,"p1","Allowance"),
        si("sb_pl4","Natural Gas Piping & Meter",1,"LS",18500),
      ),
    ),
    div("div23",
      sub("s_hv","HVAC","23-000",
        si("sb_hv1","Zoned HVAC Systems – Main House",10000,"SF",24),
        si("sb_hv2","HVAC – Pool House & Gym",1200,"SF",20),
        si("sb_hv3","Energy Recovery Ventilator (ERV)",1,"LS",18500,"p1","Needs Review"),
        si("sb_hv4","Radiant Floor Heat – Master & Baths",1,"LS",28000),
        // Deduct Alternate — omit radiant heat
        si("sb_ded3","DEDUCT: Omit Radiant Floor Heating",1,"LS",-28000,"p1","Hard Bid","Void"),
      ),
    ),
    div("div26",
      sub("s_el","Electrical","26-000",
        si("sb_el1","Electrical Rough-in – Main House",10000,"SF",24),
        si("sb_el2","Electrical – Pool House",1200,"SF",20),
        si("sb_el3","Smart Home & AV Infrastructure",1,"LS",85000,"p1","Needs Review"),
        si("sb_el4","Lighting Fixtures – Owner Allowance",1,"LS",65000,"p1","Allowance"),
        si("sb_el5","EV Charging Stations & Solar-Ready",1,"LS",22000),
        si("sb_el6","Decorative Light Fixtures",1,"LS",55000,"p1","By Others"),
      ),
    ),
    div("div32",
      sub("s_lh","Landscaping & Hardscape","32-900",
        si("sb_lh1","Pool Deck & Terrace – Limestone",4500,"SF",28,"p2"),
        si("sb_lh2","Motor Court Paving – Pavers",3200,"SF",22,"p2"),
        si("sb_lh3","Landscape – Trees, Shrubs & Sod",1,"LS",145000,"p2","Needs Review"),
        si("sb_lh4","Irrigation System – Full Property",1,"LS",28000,"p2"),
        si("sb_lh5","Landscape & Path Lighting",1,"LS",38000,"p2"),
        si("sb_lh6","Perimeter Fencing – Cedar & Steel",480,"LF",285,"p2"),
        si("sb_lh7","Entry Feature & Reflecting Pool",1,"LS",42000,"p2","Needs Review"),
        // Add Alternate — outdoor kitchen
        si("sb_alt4","ADD: Outdoor Kitchen & BBQ Station",1,"LS",38000,"p2","Hard Bid","Alt"),
      ),
    ),
  ];

  // ── Bid Leveling Demo Data ──
  const bidScopes:BidScope[]=[
    {id:"bs_sitework",name:"Sitework",divisionIds:["div2"]},
    {id:"bs_concrete",name:"Concrete",divisionIds:["div3"]},
    {id:"bs_framing",name:"Framing",divisionIds:["div6"]},
    {id:"bs_roofing",name:"Roofing",divisionIds:["div7"]},
    {id:"bs_masonry",name:"Masonry",divisionIds:["div4"]},
    {id:"bs_plumbing",name:"Plumbing",divisionIds:["div22"]},
    {id:"bs_hvac",name:"HVAC",divisionIds:["div23"]},
    {id:"bs_electrical",name:"Electrical",divisionIds:["div26"]},
  ];

  const bidPartners:BidPartner[]=[
    {id:"bp1",name:"Summit Concrete Inc.",trade:"Concrete",contact:"Mike Torres",notes:""},
    {id:"bp2",name:"Bedrock Foundations",trade:"Concrete",contact:"Sarah Chen",notes:""},
    {id:"bp3",name:"Keystone Masonry",trade:"Masonry",contact:"Dave Russell",notes:"Specializes in natural stone"},
    {id:"bp4",name:"Heritage Stone & Brick",trade:"Masonry",contact:"Tom Nakamura",notes:""},
    {id:"bp5",name:"Pacific Plumbing Co.",trade:"Plumbing",contact:"Jim Ortiz",notes:""},
    {id:"bp6",name:"Clearwater Mechanical",trade:"Plumbing",contact:"Lisa Park",notes:"Also does fire protection"},
    {id:"bp7",name:"Alpine Climate Systems",trade:"HVAC",contact:"Rob Fischer",notes:""},
    {id:"bp8",name:"Comfort Air Mechanical",trade:"HVAC",contact:"Amy Walsh",notes:""},
    {id:"bp9",name:"Brightline Electric",trade:"Electrical",contact:"Carlos Reyes",notes:"Smart home certified"},
    {id:"bp10",name:"Apex Power & Controls",trade:"Electrical",contact:"Nina Patel",notes:""},
    {id:"bp11",name:"Grademaster Earthworks",trade:"Sitework",contact:"Derek Holt",notes:"Local crew, preferred for hillside sites"},
    {id:"bp12",name:"Summit Site Solutions",trade:"Sitework",contact:"Pamela Cruz",notes:""},
    {id:"bp13",name:"Ridgeline Framing Co.",trade:"Framing",contact:"Brad Kowalski",notes:"Heavy timber specialist"},
    {id:"bp14",name:"Cascade Structural Framing",trade:"Framing",contact:"Yuki Tanaka",notes:""},
    {id:"bp15",name:"Pacific Ridge Roofing",trade:"Roofing",contact:"Steve Molina",notes:"Slate & metal certified"},
    {id:"bp16",name:"Summit Slate & Metal",trade:"Roofing",contact:"Rachel Byrne",notes:""},
    {id:"bp17",name:"Westside Concrete Works",trade:"Concrete",contact:"Frank Deluca",notes:"Strong on ICF, competitive on flatwork"},
    {id:"bp18",name:"Meridian Mechanical",trade:"HVAC",contact:"Donna Kessler",notes:"Geothermal capable"},
    {id:"bp19",name:"Ironclad Roofing Systems",trade:"Roofing",contact:"Marcus Webb",notes:"Commercial-grade slate installer, fast crew"},
    {id:"bp20",name:"Summit MEP Group",trade:"Plumbing",contact:"Dana Hirsch",notes:"Invited — awaiting proposal for plumbing scope"},
    {id:"bp21",name:"Pacific Coast Electrical",trade:"Electrical",contact:"Owen Park",notes:"Invited — proposal expected end of month"},
  ];

  const bi=(id:string,desc:string,qty:number,unit:string,uc:number,notes=""):BidItem=>({id,description:desc,qty,unit,unitCost:uc,notes});

  const bids:Bid[]=[
    // ── Concrete: Summit v1 (Superseded) ──
    {id:"sb_bid_1",partnerId:"bp1",scopeId:"bs_concrete",version:1,date:"2026-01-20",status:"Superseded",pdfName:"Summit_Concrete_v1.pdf",pdfUrl:"/demo-pdfs/summit_concrete_v1.pdf",notes:"Initial proposal",items:[
      bi("sbi_1a","Foundations & Footings – Main House",10000,"SF",24),
      bi("sbi_1b","Foundations – Pool House",1200,"SF",26),
      bi("sbi_1c","Basement Slab on Grade",2500,"SF",14),
      bi("sbi_1d","ICF Basement Walls",1,"LS",105000),
      bi("sbi_1e","Concrete Pump Truck",3,"EA",2800),
    ]},
    // ── Concrete: Summit v2 (Active) — prices came down after negotiation ──
    {id:"sb_bid_2",partnerId:"bp1",scopeId:"bs_concrete",version:2,date:"2026-02-10",status:"Active",pdfName:"Summit_Concrete_v2.pdf",pdfUrl:"/demo-pdfs/summit_concrete_v2.pdf",notes:"Revised after scope clarification",items:[
      bi("sbi_2a","Foundations & Footings – Main House",10000,"SF",22),
      bi("sbi_2b","Foundations – Pool House",1200,"SF",24),
      bi("sbi_2c","Basement Slab on Grade",2500,"SF",12),
      bi("sbi_2d","ICF Basement Walls",1,"LS",95000),
      bi("sbi_2e","Concrete Pump Truck",2,"EA",2800),
    ]},
    // ── Concrete: Bedrock v1 (Active) — competing bid, slightly higher ──
    {id:"sb_bid_3",partnerId:"bp2",scopeId:"bs_concrete",version:1,date:"2026-02-05",status:"Active",pdfName:"Bedrock_Foundations_Proposal.pdf",pdfUrl:"/demo-pdfs/bedrock_foundations_v1.pdf",notes:"",items:[
      bi("sbi_3a","Foundations & Footings – Main House",10000,"SF",23.5),
      bi("sbi_3b","Foundations – Pool House",1200,"SF",25),
      bi("sbi_3c","Basement Slab on Grade",2500,"SF",13),
      bi("sbi_3d","ICF Basement Walls",1,"LS",98000),
      // Bedrock doesn't include pump truck — shows gap in comparison
    ]},

    // ── Masonry: Keystone v1 (Active) ──
    {id:"sb_bid_4",partnerId:"bp3",scopeId:"bs_masonry",version:1,date:"2026-02-12",status:"Active",pdfName:"Keystone_Masonry_Proposal.pdf",notes:"",items:[
      bi("sbi_4a","Stone Veneer – Exterior Façade",2800,"SF",44),
      bi("sbi_4b","Fireplace Surround – Main (×2)",2,"EA",19500),
      bi("sbi_4c","Fireplace – Pool House",1,"EA",15200),
      bi("sbi_4d","Exterior Stone Steps & Retaining",1,"LS",31000),
      bi("sbi_4e","Scaffolding & Mortar Cleanup",1,"LS",8500),
    ]},
    // ── Masonry: Heritage v1 (Active) — lower on veneer, higher on fireplaces ──
    {id:"sb_bid_5",partnerId:"bp4",scopeId:"bs_masonry",version:1,date:"2026-02-18",status:"Active",pdfName:"Heritage_Stone_Brick.pdf",notes:"",items:[
      bi("sbi_5a","Stone Veneer – Exterior Façade",2800,"SF",40),
      bi("sbi_5b","Fireplace Surround – Main (×2)",2,"EA",21000),
      bi("sbi_5c","Fireplace – Pool House",1,"EA",16800),
      bi("sbi_5d","Exterior Stone Steps & Retaining",1,"LS",29500),
      // No scaffolding line — included in unit rates
    ]},

    // ── Plumbing: Pacific v1 (Active) ──
    {id:"sb_bid_6",partnerId:"bp5",scopeId:"bs_plumbing",version:1,date:"2026-02-20",status:"Active",pdfName:"Pacific_Plumbing_Proposal.pdf",notes:"Includes backflow preventers",items:[
      bi("sbi_6a","Rough Plumbing – Main House",10000,"SF",22),
      bi("sbi_6b","Rough Plumbing – Pool House",1200,"SF",18),
      bi("sbi_6c","Plumbing Fixtures – Premium",1,"LS",82000),
      bi("sbi_6d","Natural Gas Piping & Meter",1,"LS",18500),
      bi("sbi_6e","Backflow Prevention & Inspection",1,"LS",3200),
    ]},
    // ── Plumbing: Clearwater v1 (Active) — slightly higher, more detail ──
    {id:"sb_bid_7",partnerId:"bp6",scopeId:"bs_plumbing",version:1,date:"2026-02-22",status:"Active",pdfName:"Clearwater_Mechanical_Plumbing.pdf",notes:"",items:[
      bi("sbi_7a","Rough Plumbing – Main House",10000,"SF",23),
      bi("sbi_7b","Rough Plumbing – Pool House",1200,"SF",19),
      bi("sbi_7c","Plumbing Fixtures – Premium",1,"LS",88000),
      bi("sbi_7d","Natural Gas Piping & Meter",1,"LS",19200),
    ]},

    // ── HVAC: Alpine v1 (Active) ──
    {id:"sb_bid_8",partnerId:"bp7",scopeId:"bs_hvac",version:1,date:"2026-02-25",status:"Active",pdfName:"Alpine_Climate_HVAC.pdf",pdfUrl:"/demo-pdfs/alpine_climate_v1.pdf",notes:"",items:[
      bi("sbi_8a","Zoned HVAC Systems – Main House",10000,"SF",24),
      bi("sbi_8b","HVAC – Pool House & Gym",1200,"SF",20),
      bi("sbi_8c","Energy Recovery Ventilator (ERV)",1,"LS",19500),
      bi("sbi_8d","Radiant Floor Heat – Master & Baths",1,"LS",28000),
      bi("sbi_8e","Controls & Commissioning",1,"LS",6500),
    ]},
    // ── HVAC: Comfort Air v1 (Superseded) ──
    {id:"sb_bid_9",partnerId:"bp8",scopeId:"bs_hvac",version:1,date:"2026-02-15",status:"Superseded",pdfName:"ComfortAir_HVAC_v1.pdf",pdfUrl:"/demo-pdfs/comfortair_hvac_v1.pdf",notes:"Initial — did not include radiant",items:[
      bi("sbi_9a","Zoned HVAC Systems – Main House",10000,"SF",26),
      bi("sbi_9b","HVAC – Pool House & Gym",1200,"SF",22),
      bi("sbi_9c","Energy Recovery Ventilator (ERV)",1,"LS",21000),
    ]},
    // ── HVAC: Comfort Air v2 (Active) — added radiant, better pricing ──
    {id:"sb_bid_10",partnerId:"bp8",scopeId:"bs_hvac",version:2,date:"2026-03-05",status:"Active",pdfName:"ComfortAir_HVAC_v2.pdf",pdfUrl:"/demo-pdfs/comfortair_hvac_v2.pdf",notes:"Revised with radiant heat included",items:[
      bi("sbi_10a","Zoned HVAC Systems – Main House",10000,"SF",25),
      bi("sbi_10b","HVAC – Pool House & Gym",1200,"SF",21),
      bi("sbi_10c","Energy Recovery Ventilator (ERV)",1,"LS",20000),
      bi("sbi_10d","Radiant Floor Heat – Master & Baths",1,"LS",30000),
    ]},

    // ── Electrical: Brightline v1 (Active) ──
    {id:"sb_bid_11",partnerId:"bp9",scopeId:"bs_electrical",version:1,date:"2026-03-01",status:"Active",pdfName:"Brightline_Electric_Proposal.pdf",notes:"Smart home certified installer",items:[
      bi("sbi_11a","Electrical Rough-in – Main House",10000,"SF",24),
      bi("sbi_11b","Electrical – Pool House",1200,"SF",20),
      bi("sbi_11c","Smart Home & AV Infrastructure",1,"LS",82000),
      bi("sbi_11d","Lighting Fixtures – Owner Allowance",1,"LS",65000),
      bi("sbi_11e","EV Charging Stations & Solar-Ready",1,"LS",22000),
    ]},
    // ── Electrical: Apex v1 (Active) — no smart home expertise, lower base ──
    {id:"sb_bid_12",partnerId:"bp10",scopeId:"bs_electrical",version:1,date:"2026-03-03",status:"Active",pdfName:"Apex_Power_Controls.pdf",notes:"",items:[
      bi("sbi_12a","Electrical Rough-in – Main House",10000,"SF",22),
      bi("sbi_12b","Electrical – Pool House",1200,"SF",19),
      bi("sbi_12c","Smart Home & AV Infrastructure",1,"LS",95000,"Subcontracting AV portion"),
      bi("sbi_12d","Lighting Fixtures – Owner Allowance",1,"LS",65000),
      bi("sbi_12e","EV Charging Stations & Solar-Ready",1,"LS",24000),
    ]},

    // ── Sitework: Grademaster v1 (Superseded) — budgetary, pre-SD ──
    {id:"sb_bid_sw1",partnerId:"bp11",scopeId:"bs_sitework",version:1,date:"2025-09-22",status:"Superseded",pdfName:"Grademaster_Sitework_v1.pdf",notes:"Budgetary — based on topo only, no civil drawings",items:[
      bi("sbi_sw1a","Site Clearing & Demo of Existing",1,"LS",55000),
      bi("sbi_sw1b","Rough Grading & Earthwork",1,"LS",72000),
      bi("sbi_sw1c","Erosion Control & SWPPP",1,"LS",9500),
      bi("sbi_sw1d","Temporary Utilities & Access Road",1,"LS",16000),
    ]},
    // ── Sitework: Grademaster v2 (Superseded) — revised post-SD ──
    {id:"sb_bid_sw2",partnerId:"bp11",scopeId:"bs_sitework",version:2,date:"2026-01-12",status:"Superseded",pdfName:"Grademaster_Sitework_v2.pdf",notes:"Revised with SD civil drawings — retaining wall added",items:[
      bi("sbi_sw2a","Site Clearing & Demo of Existing",1,"LS",52000),
      bi("sbi_sw2b","Rough Grading & Earthwork",1,"LS",68500),
      bi("sbi_sw2c","Erosion Control & SWPPP",1,"LS",8800),
      bi("sbi_sw2d","Temporary Utilities & Access Road",1,"LS",14500),
      bi("sbi_sw2e","Retaining Wall – Rear Slope",1,"LS",38000),
    ]},
    // ── Sitework: Grademaster v3 (Active) — GMP basis ──
    {id:"sb_bid_sw3",partnerId:"bp11",scopeId:"bs_sitework",version:3,date:"2026-03-10",status:"Active",pdfName:"Grademaster_Sitework_v3.pdf",notes:"Final scope with permit-ready civil set",items:[
      bi("sbi_sw3a","Site Clearing & Demo of Existing",1,"LS",50000),
      bi("sbi_sw3b","Rough Grading & Earthwork",1,"LS",65000),
      bi("sbi_sw3c","Erosion Control & SWPPP",1,"LS",8500),
      bi("sbi_sw3d","Temporary Utilities & Access Road",1,"LS",14000),
      bi("sbi_sw3e","Retaining Wall – Rear Slope",1,"LS",36000),
    ]},
    // ── Sitework: Summit Site v1 (Active) — competing bid ──
    {id:"sb_bid_sw4",partnerId:"bp12",scopeId:"bs_sitework",version:1,date:"2026-02-20",status:"Active",pdfName:"SummitSite_Proposal.pdf",notes:"",items:[
      bi("sbi_sw4a","Site Clearing & Demo",1,"LS",53000),
      bi("sbi_sw4b","Grading & Earthwork",1,"LS",71000),
      bi("sbi_sw4c","Erosion & SWPPP",1,"LS",9000),
      bi("sbi_sw4d","Temp Utilities & Access",1,"LS",15000),
      bi("sbi_sw4e","Retaining Wall – Rear",1,"LS",41000),
    ]},

    // ── Framing: Ridgeline v1 (Superseded) — early conceptual ──
    {id:"sb_bid_fr1",partnerId:"bp13",scopeId:"bs_framing",version:1,date:"2025-10-05",status:"Superseded",pdfName:"Ridgeline_Framing_v1.pdf",notes:"Conceptual — no structural drawings yet",items:[
      bi("sbi_fr1a","Framing – Main House",10000,"SF",29),
      bi("sbi_fr1b","Framing – Pool House",1200,"SF",32),
      bi("sbi_fr1c","Roof Framing, Sheathing & Blocking",1,"LS",105000),
    ]},
    // ── Framing: Ridgeline v2 (Superseded) — revised for SD set ──
    {id:"sb_bid_fr2",partnerId:"bp13",scopeId:"bs_framing",version:2,date:"2025-12-10",status:"Superseded",pdfName:"Ridgeline_Framing_v2.pdf",notes:"Revised with SD structural — heavy timber at main entry added",items:[
      bi("sbi_fr2a","Framing – Main House",10000,"SF",28.5),
      bi("sbi_fr2b","Framing – Pool House",1200,"SF",31),
      bi("sbi_fr2c","Roof Framing, Sheathing & Blocking",1,"LS",99000),
      bi("sbi_fr2d","Heavy Timber Entry Feature",1,"LS",22000),
    ]},
    // ── Framing: Ridgeline v3 (Active) — final with IFC drawings ──
    {id:"sb_bid_fr3",partnerId:"bp13",scopeId:"bs_framing",version:3,date:"2026-03-08",status:"Active",pdfName:"Ridgeline_Framing_v3.pdf",notes:"GMP basis — full structural issued for construction",items:[
      bi("sbi_fr3a","Framing – Main House",10000,"SF",28),
      bi("sbi_fr3b","Framing – Pool House",1200,"SF",30),
      bi("sbi_fr3c","Roof Framing & Sheathing",1,"LS",95000),
      bi("sbi_fr3d","Heavy Timber Entry Feature",1,"LS",21000),
    ]},
    // ── Framing: Cascade v1 (Active) — competing bid ──
    {id:"sb_bid_fr4",partnerId:"bp14",scopeId:"bs_framing",version:1,date:"2026-02-14",status:"Active",pdfName:"Cascade_Structural_Framing.pdf",notes:"Lower SF rate, higher on roof structure",items:[
      bi("sbi_fr4a","Framing – Main Residence",10000,"SF",27.5),
      bi("sbi_fr4b","Framing – Pool House & Gym",1200,"SF",29),
      bi("sbi_fr4c","Roof Structure & Blocking",1,"LS",102000),
      bi("sbi_fr4d","Heavy Timber – Entry & Great Room",1,"LS",24000),
    ]},

    // ── Roofing: Pacific Ridge v1 (Superseded) — early budget ──
    {id:"sb_bid_rf1",partnerId:"bp15",scopeId:"bs_roofing",version:1,date:"2025-10-20",status:"Superseded",pdfName:"PacificRidge_Roofing_v1.pdf",pdfUrl:"/demo-pdfs/pacificridge_roofing_v1.pdf",notes:"Budget number — slate area TBD",items:[
      bi("sbi_rf1a","Natural Slate Roof – Main House",8500,"SF",31),
      bi("sbi_rf1b","Standing Seam Metal – Pool House",1400,"SF",27),
    ]},
    // ── Roofing: Pacific Ridge v2 (Superseded) — revised post-SD ──
    {id:"sb_bid_rf2",partnerId:"bp15",scopeId:"bs_roofing",version:2,date:"2025-12-05",status:"Superseded",pdfName:"PacificRidge_Roofing_v2.pdf",pdfUrl:"/demo-pdfs/pacificridge_roofing_v2.pdf",notes:"Revised after SD — skylights and valley flashings added",items:[
      bi("sbi_rf2a","Natural Slate Roof – Main House",8500,"SF",29),
      bi("sbi_rf2b","Standing Seam Metal – Pool House",1400,"SF",25),
      bi("sbi_rf2c","Underlayment & Flashing Package",1,"LS",22000),
    ]},
    // ── Roofing: Pacific Ridge v3 (Active) — GMP ──
    {id:"sb_bid_rf3",partnerId:"bp15",scopeId:"bs_roofing",version:3,date:"2026-02-28",status:"Active",pdfName:"PacificRidge_Roofing_v3.pdf",pdfUrl:"/demo-pdfs/pacificridge_roofing_v3.pdf",notes:"Final scope includes all skylights and custom valley work",items:[
      bi("sbi_rf3a","Natural Slate Roof – Main House",8500,"SF",28),
      bi("sbi_rf3b","Standing Seam Metal – Pool House",1400,"SF",24),
      bi("sbi_rf3c","Underlayment, Flashing & Skylight Curbs",1,"LS",24000),
    ]},
    // ── Roofing: Summit Slate v1 (Active) — competing bid ──
    {id:"sb_bid_rf4",partnerId:"bp16",scopeId:"bs_roofing",version:1,date:"2026-03-12",status:"Active",pdfName:"SummitSlate_Metal_Proposal.pdf",pdfUrl:"/demo-pdfs/summitslate_v1.pdf",notes:"Higher slate rate, lower flashing",items:[
      bi("sbi_rf4a","Slate Roofing – Main House",8500,"SF",30),
      bi("sbi_rf4b","Metal Roofing – Pool Structure",1400,"SF",26),
      bi("sbi_rf4c","Waterproofing & Flashing",1,"LS",21500),
    ]},
    // ── Roofing: Ironclad v1 (Active) — 3rd competitor ──
    {id:"sb_bid_rf5",partnerId:"bp19",scopeId:"bs_roofing",version:1,date:"2026-03-18",status:"Active",pdfName:"Ironclad_Roofing_Proposal.pdf",notes:"Fastest schedule, competitive on metal roofing",items:[
      bi("sbi_rf5a","Natural Slate Roof – Main House",8500,"SF",27.5),
      bi("sbi_rf5b","Standing Seam Metal – Pool House",1400,"SF",23),
      bi("sbi_rf5c","Underlayment, Flashings & Skylight Curbs",1,"LS",26500),
    ]},

    // ── Concrete: Westside v1 (Active) — 3rd competitor ──
    {id:"sb_bid_13",partnerId:"bp17",scopeId:"bs_concrete",version:1,date:"2026-02-22",status:"Active",pdfName:"Westside_Concrete_Proposal.pdf",pdfUrl:"/demo-pdfs/westside_concrete_v1.pdf",notes:"Competitive ICF pricing, strong on flatwork",items:[
      bi("sbi_13a","Foundations & Footings – Main House",10000,"SF",21.5),
      bi("sbi_13b","Foundations – Pool House",1200,"SF",23.5),
      bi("sbi_13c","Basement Slab on Grade",2500,"SF",11.5),
      bi("sbi_13d","ICF Basement Walls",1,"LS",92000,"Owner-supplied ICF forms — labor only"),
      bi("sbi_13e","Concrete Pump Truck",2,"EA",2600),
    ]},

    // ── HVAC: Meridian v1 (Active) — 3rd competitor, geothermal option ──
    {id:"sb_bid_14",partnerId:"bp18",scopeId:"bs_hvac",version:1,date:"2026-03-14",status:"Active",pdfName:"Meridian_Mechanical_Proposal.pdf",notes:"Includes alternate for geothermal heat pump system",items:[
      bi("sbi_14a","Zoned HVAC Systems – Main House",10000,"SF",23.5),
      bi("sbi_14b","HVAC – Pool House & Gym",1200,"SF",19.5),
      bi("sbi_14c","Energy Recovery Ventilator (ERV)",1,"LS",18500),
      bi("sbi_14d","Radiant Floor Heat – Master & Baths",1,"LS",27500),
      bi("sbi_14e","Geothermal Heat Pump Alt (Add)",1,"LS",42000,"If owner elects geo — replaces air-source units"),
      bi("sbi_14f","Controls & BMS Integration",1,"LS",8200),
    ]},
  ];

  // Trade attribution on estimate items (the new system)
  is["sb_bc1"].tradeName="Summit Concrete Inc."; is["sb_bc1"].tradeDate="2026-02-10";
  is["sb_bc2"].tradeName="Summit Concrete Inc."; is["sb_bc2"].tradeDate="2026-02-10";
  is["sb_bc3"].tradeName="Summit Concrete Inc."; is["sb_bc3"].tradeDate="2026-02-10";
  is["sb_bc4"].tradeName="Summit Concrete Inc."; is["sb_bc4"].tradeDate="2026-02-10";
  is["sb_ma1"].tradeName="Heritage Stone & Brick"; is["sb_ma1"].tradeDate="2026-02-18";
  is["sb_ma2"].tradeName="Heritage Stone & Brick"; is["sb_ma2"].tradeDate="2026-02-18";
  is["sb_ma3"].tradeName="Heritage Stone & Brick"; is["sb_ma3"].tradeDate="2026-02-18";
  is["sb_pl1"].tradeName="Pacific Plumbing Co."; is["sb_pl1"].tradeDate="2026-02-20";
  is["sb_pl2"].tradeName="Pacific Plumbing Co."; is["sb_pl2"].tradeDate="2026-02-20";
  is["sb_pl4"].tradeName="Pacific Plumbing Co."; is["sb_pl4"].tradeDate="2026-02-20";
  is["sb_hv1"].tradeName="Alpine Climate Systems"; is["sb_hv1"].tradeDate="2026-02-25";
  is["sb_hv2"].tradeName="Alpine Climate Systems"; is["sb_hv2"].tradeDate="2026-02-25";
  is["sb_hv4"].tradeName="Alpine Climate Systems"; is["sb_hv4"].tradeDate="2026-02-25";
  is["sb_el1"].tradeName="Brightline Electric"; is["sb_el1"].tradeDate="2026-03-01";
  is["sb_el2"].tradeName="Brightline Electric"; is["sb_el2"].tradeDate="2026-03-01";
  is["sb_el5"].tradeName="Brightline Electric"; is["sb_el5"].tradeDate="2026-03-01";
  is["sb_rf1"].tradeName="Pacific Ridge Roofing"; is["sb_rf1"].tradeDate="2026-02-28";
  is["sb_rf2"].tradeName="Pacific Ridge Roofing"; is["sb_rf2"].tradeDate="2026-02-28";
  is["sb_sp1"].tradeName="Grademaster Earthworks"; is["sb_sp1"].tradeDate="2026-03-10";
  is["sb_sp2"].tradeName="Grademaster Earthworks"; is["sb_sp2"].tradeDate="2026-03-10";
  is["sb_sp3"].tradeName="Grademaster Earthworks"; is["sb_sp3"].tradeDate="2026-03-10";
  is["sb_dem1"].tradeName="Grademaster Earthworks"; is["sb_dem1"].tradeDate="2026-03-10";
  is["sb_rc1"].tradeName="Ridgeline Framing Co."; is["sb_rc1"].tradeDate="2026-03-08";
  is["sb_rc2"].tradeName="Ridgeline Framing Co."; is["sb_rc2"].tradeDate="2026-03-08";
  is["sb_rc3"].tradeName="Ridgeline Framing Co."; is["sb_rc3"].tradeDate="2026-03-08";

  // Demo snapshots for Analysis tab (Schematic Design → Design Development → current)
  const scaleIs=(factor:number)=>Object.fromEntries(Object.entries(is).map(([k,v]:any)=>[k,{...v,unitCost:Math.round((v.unitCost||0)*factor)}]));
  const sdIs=scaleIs(0.78);  // Schematic: lower scope, ~78% of final
  const ddIs=scaleIs(0.91);  // Design Dev: mostly defined, ~91% of final
  // Bump certain divisions to simulate scope additions discovered in DD
  const ddBump=(ids:string[],mult:number)=>ids.forEach(k=>{if(ddIs[k])ddIs[k]={...ddIs[k],unitCost:Math.round(ddIs[k].unitCost*mult)};});
  ddBump(["sb_rf1","sb_rf2"],1.18);         // Roofing scope grew
  ddBump(["sb_el1","sb_el2","sb_el5"],1.12);// Electrical scope grew
  ddBump(["sb_fin1","sb_fin2"],0.88);       // Finishes VE'd down

  const projectSnapshots=[
    {id:"snap_sd",label:"Schematic Design",date:"2025-12-20T00:00:00Z",state:{divisions,itemState:sdIs}},
    {id:"snap_dd",label:"Design Development",date:"2026-03-15T00:00:00Z",state:{divisions,itemState:ddIs}},
  ];

  // ── Sample RFIs tied to the estimate ──
  // Helper: build a linkedItemSnapshots entry from the live item map.
  const rfiSnap=(itemId:string,divId:string,subId:string,capturedAt:string)=>{
    const li:any=is[itemId]||{};
    const d=ALL_DIVISIONS.find(x=>x.id===divId);
    const subList:any[]=(DEFAULT_SUBS as any)[divId]||[];
    const sub=subList.find(s=>s.id===subId);
    const qty=li.qty||0,unitCost=li.unitCost||0;
    return {[itemId]:{
      description:li.description||"",
      divLabel:d?.name||"",
      subLabel:sub?.name||"",
      qty,unit:li.unit||"",unitCost,
      total:qty*unitCost,
      capturedAt,
    }};
  };
  const mkRfi=(n:number,o:{
    subject:string;status:"open"|"answered"|"closed"|"void";priority:"normal"|"high"|"urgent";
    submittedBy?:string;assignedTo:string;question:string;response?:string;
    drawingRef?:string;divLabel:string;
    createdAt:string;dueDate?:string;answeredAt?:string;
    itemId:string;divId:string;subId:string;
    costImpact?:number;scheduleImpact?:number;
  }):any=>({
    id:`sb_rfi_${String(n).padStart(3,"0")}`,
    number:n,
    subject:o.subject,status:o.status,priority:o.priority,
    submittedBy:o.submittedBy||"PM Office",
    assignedTo:o.assignedTo,
    question:o.question,response:o.response||"",
    drawingRef:o.drawingRef||"",specSection:o.divLabel,
    createdAt:o.createdAt,dueDate:o.dueDate||"",answeredAt:o.answeredAt||"",
    linkedItemIds:[o.itemId],
    linkedItemSnapshots:rfiSnap(o.itemId,o.divId,o.subId,o.createdAt+"T09:00:00Z"),
    costImpact:o.costImpact||0,scheduleImpact:o.scheduleImpact||0,
  });

  const rfis=[
    mkRfi(1,{subject:"Footing depth at expansive soil zones",status:"open",priority:"normal",
      assignedTo:"Geotech / Structural",question:"Soils report calls for 36\" minimum bearing depth at the NE quadrant where expansive clay was found. Confirm whether perimeter footings in that zone need to step down or if a uniform 42\" depth is preferred.",
      drawingRef:"S-101, S-102",divLabel:"Div 03 — Concrete",createdAt:"2026-03-12",dueDate:"2026-03-19",
      itemId:"sb_bc1",divId:"div3",subId:"s_bc"}),
    mkRfi(2,{subject:"Embed plate locations at Grid B columns",status:"answered",priority:"high",
      assignedTo:"Structural Engineer",question:"Grid B steel columns conflict with the embed plate layout shown on S-301. Need clarification on whether plates can shift 4\" east to clear the column flange.",
      response:"Plates may shift up to 6\" east of shown location. Re-issue shop drawings reflecting actual placement before fabrication.",
      drawingRef:"S-301, S-303",divLabel:"Div 05 — Metals",createdAt:"2026-03-05",answeredAt:"2026-03-10",
      itemId:"sb_ss1",divId:"div5",subId:"s_ss"}),
    mkRfi(3,{subject:"Marvin Ultimate Series lead time conflict",status:"open",priority:"urgent",
      assignedTo:"Architect / Owner",question:"Marvin confirmed a 22-week lead time on the Ultimate Series — this pushes window install past the framing inspection window. Need direction: substitute equivalent product, accept schedule impact, or pre-purchase frames?",
      drawingRef:"A-401, A-402",divLabel:"Div 08 — Openings",createdAt:"2026-04-08",dueDate:"2026-04-22",
      itemId:"sb_dw1",divId:"div8",subId:"s_dw",scheduleImpact:21}),
    mkRfi(4,{subject:"Slate roofing pitch break detail",status:"answered",priority:"normal",
      assignedTo:"Architect",question:"Detail 5/A-501 shows a kicker at the pitch transition but doesn't specify ice-and-water shield extent. Confirm minimum coverage above the kicker.",
      response:"Extend ice-and-water shield 36\" above the pitch break and 24\" up the wall side. See attached detail markup.",
      drawingRef:"A-501",divLabel:"Div 07 — Thermal & Moisture Protection",createdAt:"2026-03-18",answeredAt:"2026-03-24",
      itemId:"sb_rf1",divId:"div7",subId:"s_rf"}),
    mkRfi(5,{subject:"Hot water recirculation loop routing",status:"closed",priority:"normal",
      assignedTo:"Plumbing Engineer",question:"P-201 shows recirc loop running through the master closet — owner has requested no exposed mechanical in that space. Reroute through the adjacent chase?",
      response:"Reroute through chase as proposed. Coordinate with framing for required 6\" clear chase depth.",
      drawingRef:"P-201",divLabel:"Div 22 — Plumbing",createdAt:"2026-02-26",answeredAt:"2026-03-02",
      itemId:"sb_pl1",divId:"div22",subId:"s_pl"}),
    mkRfi(6,{subject:"Subpanel location — coordination with millwork",status:"open",priority:"high",
      assignedTo:"Electrical Engineer / Architect",question:"E-101 locates the kitchen subpanel inside the pantry, but the millwork shop drawings show full-height shelving on that wall. Need an alternate location or millwork revision.",
      drawingRef:"E-101, A-301, M-Shop-04",divLabel:"Div 26 — Electrical",createdAt:"2026-04-02",dueDate:"2026-04-15",
      itemId:"sb_el1",divId:"div26",subId:"s_el",costImpact:3500}),
    mkRfi(7,{subject:"HVAC zoning at primary suite",status:"answered",priority:"normal",
      assignedTo:"Mechanical Engineer",question:"Owner has requested independent control of the primary bedroom and primary bath. Current design shows them on a shared zone — confirm we can split without resizing the equipment.",
      response:"Split is acceptable; adds one zone damper and one stat. No equipment resize needed. Update controls schedule.",
      drawingRef:"M-201, M-401",divLabel:"Div 23 — HVAC",createdAt:"2026-03-08",answeredAt:"2026-03-13",
      itemId:"sb_hv1",divId:"div23",subId:"s_hv",costImpact:1850}),
    mkRfi(8,{subject:"Salvage requirements for existing fireplace mantel",status:"open",priority:"normal",
      assignedTo:"Owner / Architect",question:"Owner mentioned an interest in salvaging the existing fireplace mantel for reuse in the library. Demo crew mobilizes 4/29. Please confirm scope and storage location before tear-down.",
      drawingRef:"AD-101",divLabel:"Div 02 — Existing Conditions",createdAt:"2026-04-18",dueDate:"2026-04-26",
      itemId:"sb_dem1",divId:"div2",subId:"s_dem"}),
    mkRfi(9,{subject:"Spray foam thickness at vaulted great room ceiling",status:"answered",priority:"high",
      assignedTo:"Architect / Energy Consultant",question:"A-501 calls for R-49 closed-cell at the vaulted ceiling, but rafter depth is only 11-7/8\". Confirm whether to fur down or accept R-44 at 7\" closed-cell.",
      response:"Fur down 2\" with continuous rigid below rafters to achieve full R-49. Adjust ceiling height by 2\" and reissue interior elevations.",
      drawingRef:"A-501, A-301",divLabel:"Div 07 — Thermal & Moisture Protection",createdAt:"2026-03-22",answeredAt:"2026-03-28",
      itemId:"sb_in1",divId:"div7",subId:"s_in",costImpact:8200}),
    mkRfi(10,{subject:"ICF wall reinforcement at SE corner",status:"open",priority:"urgent",
      assignedTo:"Structural Engineer",question:"S-201 shows #5 vertical at 16\" o.c. but the SE corner has a 14' tall basement wall with point loads from the steel frame above. Confirm reinforcement upgrade in that bay.",
      drawingRef:"S-201, S-301",divLabel:"Div 03 — Concrete",createdAt:"2026-04-15",dueDate:"2026-04-24",
      itemId:"sb_bc4",divId:"div3",subId:"s_bc",costImpact:6500}),
    mkRfi(11,{subject:"Millwork shop drawing approval — kitchen",status:"closed",priority:"normal",
      assignedTo:"Architect / Interior Designer",question:"Submitted millwork shop drawings for kitchen on 3/15. Need stamped approval to release to fabrication.",
      response:"Approved as noted. Three minor markups attached — adjust toe-kick reveal and pull centerlines per markup.",
      drawingRef:"M-Shop-01 to M-Shop-12",divLabel:"Div 06 — Wood, Plastics & Composites",createdAt:"2026-03-15",answeredAt:"2026-03-29",
      itemId:"sb_fc1",divId:"div6",subId:"s_fc"}),
    mkRfi(12,{subject:"Master bath marble — book-match orientation",status:"open",priority:"normal",
      assignedTo:"Interior Designer",question:"Stone yard has held three slabs for the master bath. Need ID to confirm book-match orientation (vertical vs. butterfly) and which wall takes the feature panel before they cut.",
      drawingRef:"A-601, ID-301",divLabel:"Div 09 — Finishes",createdAt:"2026-04-10",dueDate:"2026-04-24",
      itemId:"sb_ti1",divId:"div9",subId:"s_ti"}),
    mkRfi(13,{subject:"Pool equipment vault — location & access",status:"answered",priority:"normal",
      assignedTo:"Pool Designer / Landscape Architect",question:"L-201 places the equipment vault adjacent to the spa, but maintenance access conflicts with the proposed planting bed. Relocate to the side yard?",
      response:"Relocate to side yard per markup. Add 4' wide concrete access path. Confirm setback compliance with civil before pour.",
      drawingRef:"L-201, L-401",divLabel:"Div 13 — Special Construction",createdAt:"2026-03-25",answeredAt:"2026-04-01",
      itemId:"sb_po1",divId:"div13",subId:"s_po",costImpact:4200}),
    mkRfi(14,{subject:"Limestone pool deck — frost depth detail",status:"open",priority:"high",
      assignedTo:"Landscape Architect / Structural",question:"Local code requires 30\" frost depth, but L-301 shows the limestone deck on a 4\" slab over compacted base. Confirm whether perimeter thickened edge alone is sufficient or if full footings are required.",
      drawingRef:"L-301",divLabel:"Div 32 — Exterior Improvements",createdAt:"2026-04-12",dueDate:"2026-04-22",
      itemId:"sb_lh1",divId:"div32",subId:"s_lh",costImpact:7800}),
    mkRfi(15,{subject:"Sliding glass wall pocket detail",status:"answered",priority:"normal",
      assignedTo:"Architect / Window Manufacturer",question:"Marvin shop drawings show a 6-1/2\" pocket depth, but A-301 shows 5\" wall thickness. Confirm whether wall is to be furred or pocket assembly is to be reduced.",
      response:"Fur the pocket wall to 7\" total depth — see revised A-301. No change to exterior wall plane.",
      drawingRef:"A-301, M-Shop-W04",divLabel:"Div 08 — Openings",createdAt:"2026-03-30",answeredAt:"2026-04-05",
      itemId:"sb_dw4",divId:"div8",subId:"s_dw"}),
    mkRfi(16,{subject:"Drainage swale at NE corner",status:"open",priority:"normal",
      assignedTo:"Civil Engineer",question:"Existing grade at the NE corner pitches toward the new foundation. C-101 doesn't show a swale or drain in that area. Need solution before backfill.",
      drawingRef:"C-101, C-301",divLabel:"Div 02 — Existing Conditions",createdAt:"2026-04-14",dueDate:"2026-04-28",
      itemId:"sb_sp1",divId:"div2",subId:"s_sp",costImpact:3200}),
    mkRfi(17,{subject:"Quartzite countertop seam locations",status:"closed",priority:"normal",
      assignedTo:"Interior Designer",question:"Slab yard has confirmed two slabs available for the kitchen island. Need ID to mark preferred seam locations on the slab photos before fabrication.",
      response:"Seam locations marked and approved on attached slab photos. Proceed with fabrication.",
      drawingRef:"ID-401",divLabel:"Div 09 — Finishes",createdAt:"2026-03-02",answeredAt:"2026-03-08",
      itemId:"sb_st1",divId:"div9",subId:"s_st"}),
    mkRfi(18,{subject:"Stone veneer flashing at parapet",status:"open",priority:"normal",
      assignedTo:"Architect",question:"Detail 7/A-502 shows through-wall flashing terminating below the cap stone — but doesn't address weep locations. Confirm weep spacing and whether weeps should be concealed or exposed.",
      drawingRef:"A-502",divLabel:"Div 04 — Masonry",createdAt:"2026-04-16",dueDate:"2026-04-30",
      itemId:"sb_ma1",divId:"div4",subId:"s_ma"}),
    mkRfi(19,{subject:"Radiant floor manifold location",status:"answered",priority:"high",
      assignedTo:"Mechanical Engineer",question:"M-301 shows the radiant manifold in the mechanical room, but loop lengths to the master suite exceed 300'. Relocate manifold to the upstairs linen closet?",
      response:"Add a secondary manifold in the upstairs linen closet for the master suite loops. Re-issue M-301 reflecting two-manifold layout.",
      drawingRef:"M-301",divLabel:"Div 23 — HVAC",createdAt:"2026-03-20",answeredAt:"2026-03-26",
      itemId:"sb_hv4",divId:"div23",subId:"s_hv",costImpact:2400}),
    mkRfi(20,{subject:"DEDUCT — radiant floor heating (voided)",status:"void",priority:"normal",
      assignedTo:"Owner",question:"Initial RFI to confirm pricing for omitting radiant floor heating system.",
      response:"Owner has elected to keep radiant heating. RFI voided — no further action.",
      drawingRef:"M-301",divLabel:"Div 23 — HVAC",createdAt:"2026-03-04",answeredAt:"2026-03-06",
      itemId:"sb_ded3",divId:"div23",subId:"s_hv"}),
  ];

  const siteLogistics=buildSandboxSiteLogistics();

  return {
    projectName:"100 Evergreen Lane - Residence",
    buildingArea:11200,
    duration:24,
    overhead:12,
    profit:10,
    contingency:7,
    proposals:[{id:"p1",name:"Main House",color:0},{id:"p2",name:"Pool House",color:1}],
    proposalAreas:{p1:10000,p2:1200},
    divisions,
    itemState:is,
    bidScopes,
    bidPartners,
    bids,
    siteLogistics,
    levelingAdjustments:[
      {id:"la_1",scopeId:"bs_concrete",description:"Temporary shoring",
        partners:{bp1:{included:false,amount:18000},bp2:{included:true,amount:0}}},
      {id:"la_2",scopeId:"bs_concrete",description:"Site dewatering",
        partners:{bp1:{included:true,amount:0},bp2:{included:false,amount:12000}}},
      {id:"la_3",scopeId:"bs_framing",description:"Temporary bracing",
        partners:{bp5:{included:false,amount:8500},bp6:{included:true,amount:0}}},
    ],
    projectSnapshots,
    rfis,
  };
}

// Demo org-wide Directory entries that match the sandbox estimate's trade partners.
// Seeded into orgPartners when the sandbox project is loaded so the picker is populated.
export const SANDBOX_ORG_PARTNERS=[
  {id:"sbp_1",name:"Summit Concrete Inc.",   trade:"Concrete",        contact:"Mike Deluca",    phone:"(503) 555-0101",email:"mike@summitconcrete.com",    notes:"Preferred concrete sub — good track record on complex SOG pours."},
  {id:"sbp_2",name:"Heritage Stone & Brick", trade:"Masonry",         contact:"Teresa Okafor",  phone:"(503) 555-0202",email:"teresa@heritagestone.com",   notes:"Specialty in thin-set stone veneer and masonry chimneys."},
  {id:"sbp_3",name:"Pacific Plumbing Co.",   trade:"Plumbing",        contact:"Dave Saenz",     phone:"(503) 555-0303",email:"dave@pacificplumbing.com",   notes:"Full rough-in and trim-out. Good on radiant/HW systems."},
  {id:"sbp_4",name:"Alpine Climate Systems", trade:"HVAC",            contact:"Sara Whitfield",  phone:"(503) 555-0404",email:"sara@alpineclimate.com",     notes:"Handles both forced-air and radiant zoning. Reliable on BAS tie-ins."},
  {id:"sbp_5",name:"Brightline Electric",    trade:"Electrical",      contact:"James Park",      phone:"(503) 555-0505",email:"james@brightlineelec.com",  notes:"Licensed C-10. Strong on low-voltage integration and EV infrastructure."},
  {id:"sbp_6",name:"Pacific Ridge Roofing",  trade:"Roofing",         contact:"Carla Nguyen",   phone:"(503) 555-0606",email:"carla@pacificridgeroofing.com",notes:"Certified for slate, standing-seam, and torch-down. Competitive on tile."},
  {id:"sbp_7",name:"Grademaster Earthworks", trade:"Site / Grading",  contact:"Tom Harrington", phone:"(503) 555-0707",email:"tom@grademaster.com",        notes:"Site prep, grading, drainage, and compacted base. Owns equipment."},
  {id:"sbp_8",name:"Ridgeline Framing Co.",  trade:"Rough Carpentry", contact:"Ben Foster",     phone:"(503) 555-0808",email:"ben@ridgelineframing.com",   notes:"Complex roof framing specialty. Works well with post-and-beam hybrid scopes."},
];

export const LS_PRESETS_KEY_EXPORT="oe_presets_v1";

export function buildProjectFromPreset(name, preset){
  const divSettings=ALL_DIVISIONS.map(d=>({id:d.id,enabled:(preset.divisions||[]).includes(d.id)}));
  const ss=Object.fromEntries(Object.entries(DEFAULT_SUBS).map(([k,v])=>[k,v.map(s=>({...s}))]));
  const {divisions:divs,itemState:is}=buildFreshProject(divSettings,ss);
  return {
    projectName:name||"New Project",
    buildingArea:0,duration:12,
    overhead:preset.overhead||10,profit:preset.profit||8,contingency:preset.contingency||5,
    proposals:[{id:"p1",name:"General Construction",color:0}],
    proposalAreas:{},divisionSettings:divSettings,subSettings:ss,divisions:divs,itemState:is,
  };
}
